import jobApplications from './api/jobApplications';
import dailyReminderApi from "./api/dailyReminder";
import React, { useEffect, useRef, Suspense, lazy, useState } from "react";
import { Provider as PaperProvider } from "react-native-paper";
import { View, TouchableOpacity, Text, Modal, ScrollView, AccessibilityInfo, TextInput, Platform, ActivityIndicator } from "react-native";
import { Chip, Button } from "react-native-paper";
import { useNavigate, useLocation } from "react-router-dom";
import { tabToPath, pathToTab, pathToRestoredTab } from "./utils/routeMap";
import "./App.css"; // Import CSS for styling

// Import theme
import { theme } from "./theme/theme";

// Import custom icon component
import Icon from "./components/Icon";
import ContactAvatar from "./components/ContactAvatar";
import { FilterChip } from "./components/FilterComponents";
import SearchSuggestionList from "./components/SearchSuggestionList";
import { useAccessibility } from "./hooks/useAccessibility";
import HamburgerMenu from "./components/HamburgerMenu";
import CollapsibleQuickAccess from "./components/CollapsibleQuickAccess";
import { showAlert } from "./utils/appAlert";
import { authApi, listItemsApi, tradingProfilesApi, reviewsApi, servicesApi, eventsApi, jobsApi, notificationsApi, feedbackApi, messagesApi } from "./api";
import { unsubscribeFromPush } from "./utils/pushRegistration";
import AlertModal from "./components/AlertModal";
import UpdateBanner from "./components/UpdateBanner";
import useAppUpdate from "./hooks/useAppUpdate";
import InstallGuide from "./components/InstallGuide";
import useInstallNudge from "./hooks/useInstallNudge";
import { useAppContext } from "./context/AppContext";
import { australianLocations } from "./data/locations";
import { styles } from "./styles/appStyles";
import AppFab from "./components/AppFab";
import AppBackground from "./components/AppBackground";
import { getOrganizerProfile } from "./data/organizerProfiles";
import { useEmergencyNotice } from "./hooks/useEmergencyNotice";
import { useLocationModal } from "./hooks/useLocationModal";
import { useTestimonialsModal } from "./hooks/useTestimonialsModal";

// Lazy-loaded screens — each gets its own chunk
const ServicesScreen = lazy(() => import("./screens/ServicesScreen"));
const SponsoredListingsScreen = lazy(() => import("./screens/SponsoredListingsScreen"));
const SkillsNeededScreen = lazy(() => import("./screens/SkillsNeededScreen"));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen"));
const NotificationsScreen = lazy(() => import("./screens/NotificationsScreen"));
const AdminFeedbackScreen = lazy(() => import("./screens/AdminFeedbackScreen"));
const IconReferenceScreen = lazy(() => import("./screens/IconReferenceScreen"));
const MessagesScreen = lazy(() => import("./screens/MessagesScreen"));
const AddServiceScreen = lazy(() => import("./screens/AddServiceScreen"));
const AddEventScreen = lazy(() => import("./screens/AddEventScreen"));
const ServiceSuccessScreen = lazy(() => import("./screens/ServiceSuccessScreen"));
const AddAdvertisingScreen = lazy(() => import("./screens/AddAdvertisingScreen"));
const SponsoredAdScreen = lazy(() => import("./screens/SponsoredAdScreen"));
const SettingsScreen = lazy(() => import("./screens/SettingsScreen"));
const AboutScreen = lazy(() => import("./screens/AboutScreen"));
const CommunityCalendarScreen = lazy(() => import("./screens/CommunityCalendarScreen"));
const CommunityChatScreen = lazy(() => import("./screens/CommunityChatScreen"));
const HomeScreen = lazy(() => import("./screens/HomeScreen"));
const WaitlistScreen = lazy(() => import("./screens/WaitlistScreen"));
const SignInScreen = lazy(() => import("./screens/SignInScreen"));
const SignUpScreen = lazy(() => import("./screens/SignUpScreen"));
const VerificationScreen = lazy(() => import("./screens/VerificationScreen"));
const WelcomeScreen = lazy(() => import("./screens/WelcomeScreen"));
const OnboardingScreen = lazy(() => import("./screens/OnboardingScreen"));
const ForgotPasswordScreen = lazy(() => import("./screens/ForgotPasswordScreen"));
const ResetPasswordScreen = lazy(() => import("./screens/ResetPasswordScreen"));
const ClaimBusinessScreen = lazy(() => import("./screens/ClaimBusinessScreen"));
const MessageDetailsScreen = lazy(() => import("./screens/MessageDetailsScreen"));
const FeedbackScreen = lazy(() => import("./screens/FeedbackScreen"));
const StyleGuideScreen = lazy(() => import("./screens/StyleGuideScreen"));
const UxStyleGuideScreen = lazy(() => import("./screens/UxStyleGuideScreen"));
const TermsOfService = lazy(() => import("./legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./legal/PrivacyPolicy"));
const ChangePasswordScreen = lazy(() => import("./screens/ChangePasswordScreen"));
const PaymentMethodsScreen = lazy(() => import("./screens/PaymentMethodsScreen"));
const DatePickerScreen = lazy(() => import("./screens/DatePickerScreen"));
const MyListingsScreen = lazy(() => import("./screens/MyListingsScreen"));
const FavoritesScreen = lazy(() => import("./screens/FavoritesScreen"));
const AdminDashboardScreen = lazy(() => import("./screens/AdminDashboardScreen"));
const FlaggedReviewsScreen = lazy(() => import("./screens/FlaggedReviewsScreen"));
const FlaggedListingsScreen = lazy(() => import("./screens/FlaggedListingsScreen"));
const TechStackScreen = lazy(() => import("./screens/TechStackScreen"));
const UserManagementScreen = lazy(() => import("./screens/UserManagementScreen"));
const UserDetailsScreen = lazy(() => import("./screens/UserDetailsScreen"));
const AnalyticsScreen = lazy(() => import("./screens/AnalyticsScreen"));
const PendingItemsScreen = lazy(() => import("./screens/PendingItemsScreen"));
const TradingProfileScreen = lazy(() => import("./screens/TradingProfileScreen"));
const OpenToScreen = lazy(() => import("./screens/OpenToScreen"));
const PersonalProfileScreen = lazy(() => import("./screens/PersonalProfileScreen"));
const TutorialScreen = lazy(() => import("./screens/TutorialScreen"));
const TutorialScreenMinimal = lazy(() => import("./screens/TutorialScreenMinimal"));
const BarteringGuideScreen = lazy(() => import("./screens/BarteringGuideScreen"));
const ReviewScreen = lazy(() => import("./screens/ReviewScreen"));
const PaymentScreen = lazy(() => import("./screens/PaymentScreen"));
const QRCodeScreen = lazy(() => import("./screens/QRCodeScreen"));
const ListingDetailsScreen = lazy(() => import("./screens/ListingDetailsScreen"));
const ContactsScreen = lazy(() => import("./screens/ContactsScreen"));
const JobApplicationsScreen = lazy(() => import("./screens/JobApplicationsScreen"));
const MyListScreen = lazy(() => import("./screens/MyListScreen"));
const QuickLinksScreen = lazy(() => import("./screens/QuickLinksScreen"));
const LocationsScreen = lazy(() => import("./screens/LocationsScreen"));
const AdvertisementsScreen = lazy(() => import("./screens/AdvertisementsScreen"));
const InviteCodesScreen = lazy(() => import("./screens/InviteCodesScreen"));
const WaitlistAdminScreen = lazy(() => import("./screens/WaitlistAdminScreen"));
const PhoneChecksScreen = lazy(() => import("./screens/PhoneChecksScreen"));
const ProviderResponse = lazy(() => import("./components/ProviderResponse"));
const ProviderResponseInput = lazy(() => import("./components/ProviderResponseInput"));

const ADMIN_TABS = new Set([
  "admin-dashboard",
  "pending-items",
  "analytics",
  "user-management",
  "user-details",
  "flagged-reviews",
  "flagged-listings",
  "tech-stack",
  "invite-codes",
  "waitlist-admin",
  "phone-checks",
]);

// Stands in for the three payment screens outside development. They are mocks —
// sample cards held in local state, a "Mock payment processing" path, and a
// Bitcoin address that goes nowhere — so a member reaching one was being asked
// for a card number that would be thrown away. Hiding the Settings row is not
// enough on its own, because these tabs have their own URLs. Remove this once a
// real payment provider is wired in. See 5_LEGAL_EXPOSURE.md.
const PaymentsUnavailable = () => (
  <View style={styles.paymentsUnavailable}>
    <Text style={styles.paymentsUnavailableTitle}>Not available yet</Text>
    <Text style={styles.paymentsUnavailableBody}>
      Outback Exchange is free while we're getting started. There's nothing to
      pay for, so there's nothing to set up here.
    </Text>
  </View>
);

const App = () => {
  // Watches for a newer deploy. Members have no reason to know that relaunching
  // is how they get new code, so the app has to tell them.
  const { updateReady, applyUpdate, dismissUpdate } = useAppUpdate();

  // ── All state from AppContext ──────────────────────────────────────
  const {
    activeTab, setActiveTab,
    user, setUser,
    isFirstTimeUser, setIsFirstTimeUser,
    isInTutorialFlow, setIsInTutorialFlow,
    showProfileTutorial, setShowProfileTutorial,
    profileTutorialKey,
    selectedItem, setSelectedItem,
    selectedUser, setSelectedUser,
    selectedMessage, setSelectedMessage,
    deleteMessageCallback, setDeleteMessageCallback,
    selectedServiceProvider, setSelectedServiceProvider,
    sponsoredAdData, setSponsoredAdData,
    sponsoredAdSource, setSponsoredAdSource,
    isProfileReadOnly, setIsProfileReadOnly,
    personalProfileComplete, setPersonalProfileComplete,
    tradingProfileComplete, setTradingProfileComplete,
    openToPreferences, setOpenToPreferences,
    profileRefreshKey, setProfileRefreshKey,
    favorites, setFavorites,
    addItemType, setAddItemType,
    editItemData, setEditItemData,
    successData, setSuccessData,
    feedbackInitialData, setFeedbackInitialData,
    addAdvertisingKeyCounter, setAddAdvertisingKeyCounter,
    locationsScreenData, setLocationsScreenData,
    advertisementsScreenData, setAdvertisementsScreenData,
    tradingProfileInitialTab, setTradingProfileInitialTab,
    scrollToJobBoard, setScrollToJobBoard,
    scrollToServices, setScrollToServices,
    scrollToJobBoardSection, setScrollToJobBoardSection,
    jobsListRefreshKey, setJobsListRefreshKey,
    datePickerCallbackRef,
    selectedExpiryDate, setSelectedExpiryDate,
    paymentMethodView, setPaymentMethodView,
    pendingItemsFilter, setPendingItemsFilter,
    pendingItemsData, setPendingItemsData,
    showTrialBanner, setShowTrialBanner,
    shouldShowTrialBanner, dismissTrialBanner,
    showAddMenu, setShowAddMenu,
    showHamburgerMenu, setShowHamburgerMenu,
    showTerms, setShowTerms,
    showPrivacy, setShowPrivacy,
    showSignOutModal, setShowSignOutModal,
    alertModal, setAlertModal,
    unreadNotifications, unreadMessages, loadUnreadMessageCount, adjustUnreadNotificationCount,
    myListUnseenCount, setMyListUnseenCount,
    servicesScreenFilters, setServicesScreenFilters,
    skillsNeededScreenFilters, setSkillsNeededScreenFilters,
    sponsoredListingsScreenFilters, setSponsoredListingsScreenFilters,
    cachedJobs, setCachedJobs,
    cachedSponsoredAds, setCachedSponsoredAds,
    cachedEvents, setCachedEvents,
    servicesScrollY, setServicesScrollY,
    trialDaysLeft,
    showDevFeatures,
  } = useAppContext();

  const canPreviewSubscription = user?.role === "admin" ||
    user?.email?.trim().toLowerCase() === "williamssubscriptions@proton.me";

  const [privateJobsAccess, setPrivateJobsAccess] = useState(null);
  const canAccessPrivateJobs = Boolean(user?.id && privateJobsAccess?.userId === user.id && privateJobsAccess?.allowed);
  useEffect(() => {
    let cancelled = false;
    setPrivateJobsAccess(null);
    if (user?.id) jobApplications.canAccess().then(allowed => {
      if (!cancelled) setPrivateJobsAccess({userId:user.id,allowed});
    }).catch(() => {});
    return () => {cancelled=true;};
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const sync = () => {
      if (document.visibilityState === "visible") dailyReminderApi.syncTimeZone().catch(() => {});
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
    };
  }, [user?.id]);

  // Nudges a member towards installing the app and allowing notifications.
  // Until both are done, alerts cannot reach an iPhone at all.
  const installNudge = useInstallNudge(Boolean(user));
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const previousTabRef = useRef("services");
  const inboxOriginRef = useRef("services");
  useEffect(() => {
    if (!["messages", "notifications", "message-details"].includes(activeTab)) inboxOriginRef.current = activeTab;
  }, [activeTab]);
  // Where the review form was opened from, which is not the same question as
  // previousTabRef answers. previousTabRef deliberately holds the tab *before* a
  // detail view so the back chain survives; leaving a review from a listing or a
  // profile has to come back to that listing or profile, not to whatever came
  // before it.
  const reviewOriginTabRef = useRef(null);
  // The path the browser actually loaded, captured on first render — the URL
  // sync below rewrites the address bar, so this is the only record of where
  // the user was before they refreshed.
  const bootPathRef = useRef(location.pathname);
  const [authBootstrapped, setAuthBootstrapped] = useState(false);
  const [pendingSignInNavigation, setPendingSignInNavigation] = useState(false);
  const [inviteCodeFromUrl, setInviteCodeFromUrl] = useState(null);

  // Supabase's hosted GoTrue has a long-standing bug (supabase/auth#1738) where
  // password-recovery links sometimes ignore the configured redirectTo and land
  // on the bare site URL instead of /reset-password, even though the exact URL
  // is in the allow list. Since the recovery session tokens are appended as a
  // URL hash fragment (#...&type=recovery) which survives regardless of which
  // path GoTrue redirects to, detect that fragment directly instead of trusting
  // the path — this lets us show the reset form no matter where the link lands.
  const isRecoveryLink = location.hash.includes("type=recovery");

  // Sync activeTab → URL (handles changes from context effects too)
  useEffect(() => {
    // Hold off until the session has resolved. activeTab starts at "sign-in",
    // so running this during boot would rewrite the URL of whatever page was
    // refreshed before we've had a chance to restore it.
    if (!authBootstrapped) return;
    // Signed-out screens are all chosen from the path, not from activeTab. Without
    // this the landing page at "/" would be replaced by "/sign-in" the moment the
    // session resolved, because activeTab still holds its default "sign-in".
    if (!user) return;
    // Auth-only screens (sign-in, sign-up, forgot-password, reset-password, verify)
    // are rendered straight from the URL and aren't tracked by activeTab. Skip the
    // forced redirect here, otherwise a fresh page load of e.g. /reset-password gets
    // immediately overwritten by activeTab's default "sign-in" value.
    if (pathToTab(location.pathname) === null || isRecoveryLink) return;
    const targetPath = tabToPath(activeTab);
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [activeTab, authBootstrapped, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL → activeTab on browser back/forward
  useEffect(() => {
    const tab = pathToTab(location.pathname);
    if (tab && tab !== activeTab && user) {
      setActiveTab(tab);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle invite code from URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteCode = params.get("invite");
    
    if (inviteCode) {
      const normalized = inviteCode.toUpperCase().trim();
      setInviteCodeFromUrl(normalized);
      
      // If not on signup screen, redirect there
      if (location.pathname !== "/sign-up") {
        navigate("/sign-up", { replace: true });
      }
    }
  }, [location.search, location.pathname, navigate]);

  // Share App QR lands on /sign-up. If this phone is already a member, skip the
  // form and open the app. Logged-out visitors at "/" get the landing page.
  useEffect(() => {
    if (!authBootstrapped || !user || !user.verified || isRecoveryLink) return;
    if (location.pathname !== "/sign-up" && location.pathname !== "/sign-in") return;
    navigate(tabToPath(activeTab) || "/", { replace: true });
  }, [authBootstrapped, user, location.pathname, isRecoveryLink, activeTab, navigate]);

  // Accessibility hook
  const { screenReaderEnabled } = useAccessibility();

  const {
    activeEmergencyNotice,
    handleDismissEmergencyNotice,
    handleViewEmergencyNoticeDetails,
  } = useEmergencyNotice(user, { setSelectedItem, setActiveTab });

  // Accessibility announcement function
  const announceToScreenReader = (message) => {
    if (screenReaderEnabled) {
      AccessibilityInfo.announceForAccessibility(message);
    }
  };

  const {
    showLocationModal,
    setShowLocationModal,
    locationSearchQuery,
    setLocationSearchQuery,
    filteredLocations,
    searchInputRef,
    selectedLocationsInModal,
    setSelectedLocationsInModal,
    isLocationMultiSelect,
    locationSelectCallback,
    handleLocationSelect,
    pickLocationFromSearch,
    pickTopSearchResult,
    openLocationModal,
    handleModalLocationSelect,
    closeLocationModal,
  } = useLocationModal(announceToScreenReader);

  const {
    showTestimonialsModal,
    setShowTestimonialsModal,
    showTestimonialsSearch,
    setShowTestimonialsSearch,
    testimonialsSearchQuery,
    setTestimonialsSearchQuery,
    modalTestimonials,
    modalProviderName,
    modalProviderId,
    modalRespondingToReviewId,
    setModalRespondingToReviewId,
    testimonials,
    currentTestimonials,
    filteredTestimonials,
    testimonialsStats,
    openTestimonialsModal,
    handleResponseSubmit,
    resetTestimonialsModal,
  } = useTestimonialsModal(announceToScreenReader, { selectedItem, setSelectedItem, isProfileReadOnly });

  // Visual Viewport tracking for mobile keyboard handling
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      return window.visualViewport.height;
    }
    return typeof window !== 'undefined' ? window.innerHeight : 0;
  });
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [organiserActionsVisible, setOrganiserActionsVisible] = useState(false);
  const [myListOpenTab, setMyListOpenTab] = useState(null);
  const [adminHubView, setAdminHubView] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    const handleViewportResize = () => {
      setViewportHeight(window.visualViewport.height);
    };

    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);

    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      window.visualViewport.removeEventListener('scroll', handleViewportResize);
    };
  }, []);

  // Web styles for browser compatibility using CSS-in-JS
  const webStyles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      // A definite, non-shrinking height keeps nested flex screens visible
      // when Paper's portal host has no explicit height (notably Safari).
      height: viewportHeight > 0 ? viewportHeight : '100dvh',
      minHeight: 0,
      overflow: 'hidden',
      flex: '0 0 auto',
      backgroundColor: 'transparent',
      position: 'relative',
    },
    debugHeader: {
      backgroundColor: '#000000',
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 16,
      paddingRight: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    debugText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      fontFamily: 'Poppins',
      textAlign: 'center',
    },
    content: {
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      paddingLeft: 0,
      paddingRight: 0,
      marginLeft: 0,
      marginRight: 0,
      padding: 0,
      margin: 0,
      position: "relative",
      zIndex: 2,
      backgroundColor: 'transparent',
    },
    appContentLayer: {
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      position: 'relative',
      zIndex: 2,
      backgroundColor: 'transparent',
    },
    contentFullScreen: {
      flex: 1,
    },
  };

  // currentTestimonials, filteredTestimonials, testimonialsStats from useTestimonialsModal

  // Define root container component based on platform
  const RootContainer = Platform.OS === 'web' ? 'div' : View;
  const ContentContainer = Platform.OS === 'web' ? 'div' : View;

  // openTestimonialsModal + handleResponseSubmit from useTestimonialsModal

  // FAB handlers for services screen
  const handleFabPress = () => {
    setShowAddMenu(!showAddMenu);
  };

  const isStaffUser =
    user?.role === "admin" ||
    user?.role === "moderator" ||
    user?.isAdmin === true;

  const profileToUserData = (profile) => ({
    id: profile.id,
    name: `${profile.first_name || ""} ${profile.last_name || ""}`.trim(),
    first_name: profile.first_name,
    last_name: profile.last_name,
    email: profile.email,
    phone: profile.phone,
    role: profile.role,
    email_verified: profile.email_verified,
    location_verified: profile.location_verified,
    bio: profile.bio || null,
    location: profile.location || null,
    profileImageUrl: profile.profile_image_url,
    businesses: profile.businesses,
    subscriptions: profile.subscriptions,
  });

  // Reset scrollToJobBoard after ProfileScreen has handled it
  useEffect(() => {
    if (scrollToJobBoard && activeTab === "profile") {
      const timer = setTimeout(() => {
        console.log("[App] Resetting scrollToJobBoard to false");
        setScrollToJobBoard(false);
      }, 3000); // Increased timeout to give more time for scrolling
      return () => clearTimeout(timer);
    }
  }, [scrollToJobBoard, activeTab]);

  // Reset scrollToServices after ProfileScreen has handled it
  useEffect(() => {
    if (scrollToServices && activeTab === "profile") {
      const timer = setTimeout(() => {
        console.log("[App] Resetting scrollToServices to false");
        setScrollToServices(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [scrollToServices, activeTab]);

  // Reset scrollToJobBoardSection after TradingProfileScreen has handled it
  useEffect(() => {
    if (scrollToJobBoardSection && activeTab === "trading-profile") {
      const timer = setTimeout(() => {
        console.log("[App] Resetting scrollToJobBoardSection to false");
        setScrollToJobBoardSection(false);
      }, 3000); // Increased timeout to give more time for scrolling
      return () => clearTimeout(timer);
    }
  }, [scrollToJobBoardSection, activeTab]);

  // My List unseen-match badge (API-dependent, stays in App.js)
  useEffect(() => {
    if (!user || !user.verified) {
      setMyListUnseenCount(0);
      return;
    }
    if (activeTab === "my-list") {
      setMyListUnseenCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await listItemsApi.unseenCount();
        if (!cancelled && result.success) {
          setMyListUnseenCount(Number(result.data?.count) || 0);
        }
      } catch {
        // best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToSettings = () => {
    previousTabRef.current = activeTab;
    setActiveTab("settings");
  };

  const navigateToHelpSupport = () => {
    previousTabRef.current = activeTab;
    setActiveTab("about");
  };

  const navigateToAbout = () => {
    previousTabRef.current = activeTab;
    setActiveTab("about");
  };

  const handleUserDeactivate = (userId, isDeactivated) => {
    // Update selectedUser state
    if (selectedUser) {
      const updatedUser = {
        ...selectedUser,
        isDeactivated: isDeactivated,
      };
      setSelectedUser(updatedUser);
    }

    // Store deactivated state in localStorage
    try {
      const deactivatedUsers = JSON.parse(
        typeof window !== "undefined" 
          ? localStorage.getItem("deactivated_users") || "[]"
          : "[]"
      );
      
      const userIdentifier = userId; // userId can be id, email, or phone
      
      if (isDeactivated) {
        // Add to deactivated list if not already present
        if (!deactivatedUsers.includes(userIdentifier)) {
          deactivatedUsers.push(userIdentifier);
        }
      } else {
        // Remove from deactivated list
        const index = deactivatedUsers.indexOf(userIdentifier);
        if (index > -1) {
          deactivatedUsers.splice(index, 1);
        }
      }
      
      if (typeof window !== "undefined") {
        localStorage.setItem("deactivated_users", JSON.stringify(deactivatedUsers));
      }
    } catch (error) {
      console.error("Error updating deactivated users:", error);
    }
  };

  const handleSignIn = (userData, options = {}) => {
    const { redirect = true } = options;
    // The DB (users row, fetched at sign-in) is the source of truth. Only fall
    // back to the locally-cached personal profile when it provably belongs to
    // THIS user — otherwise a previous account's cached image/email/name bleeds
    // into the one signing in now. API fields always win over the local cache.
    let savedProfile = {};
    try {
      const stored = localStorage.getItem("personalProfile");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.__ownerId && parsed.__ownerId === userData.id) {
          savedProfile = parsed;
        } else {
          // Stale cache from a different account — drop it.
          localStorage.removeItem("personalProfile");
        }
      }
    } catch (e) {
      console.error("Failed to restore personal profile:", e);
    }

    const userWithAdminRights = {
      // Local cache first (lowest precedence) …
      ...savedProfile,
      // … then the authoritative API record wins for every overlapping field.
      ...userData,
      // Normalise the avatar so screens that read `profileImage` get the DB value.
      profileImage:
        userData.profileImage || userData.profileImageUrl || savedProfile.profileImage || null,
      // For fields that live in both the DB and localStorage, prefer the DB value
      // when it's non-empty; otherwise fall back to the cached value so pre-existing
      // accounts (with null location/bio in the DB) don't lose their saved data.
      // Final fallback for location: default to the community home so "Near X"
      // headers always show even before the user has saved their profile.
      location: userData.location || savedProfile.location || "Glenwood, QLD",
      bio: userData.bio || savedProfile.bio || null,
      // A confirmed email address is the only thing that counts as verified.
      // Location and a saved profile say nothing about whether the address is
      // real, and ORing them in here waved every new account straight past the
      // verification screen — including ones whose code never arrived.
      verified: Boolean(userData.email_verified),
      isAdmin: userData.role === "admin" || userData.role === "moderator",
      // Free launch: keep legacy fields inert so old screens don't infer a paid trial.
      trialDays: userData.trialDays || 0,
      signUpDate: userData.signUpDate || new Date().toISOString(),
      trialEndDate: userData.trialEndDate || null,
      signedUpWithReferral: userData.signedUpWithReferral || false,
      // Initialize membershipEarnings if not present
      membershipEarnings: userData.membershipEarnings || {
        referrals: [],
        ratingsGiven: [],
        monthsEarned: 0,
        currentEarnedMembershipEndDate: null,
      },
    };
    setUser(userWithAdminRights);
    
    // Queue navigation for after user state has propagated (prevents white screen race condition)
    if (redirect) {
      setPendingSignInNavigation(true);
    }

    // Don't wait for the 30s poll — the badge should be right as soon as you're in.
    loadUnreadMessageCount();

    // Load the persisted "Open To" preferences from the DB (source of truth)
    // so they survive reload instead of resetting to the default each session.
    if (userWithAdminRights.id) {
      tradingProfilesApi.get(userWithAdminRights.id).then((result) => {
        if (result.success && result.data?.exchange_preferences) {
          const prefs = result.data.exchange_preferences;
          if (Array.isArray(prefs) && prefs.length) {
            setOpenToPreferences(prefs);
          }
        }
      });
    }
  };

  const handleSignUp = (userData) => {
    // Check for referral code in URL
    const referralId = typeof window !== 'undefined' 
      ? new URLSearchParams(window.location.search).get('ref')
      : null;
    
    const signUpDate = new Date().toISOString();
    
    // Initialize membershipEarnings
    const newUser = {
      ...userData,
      signUpDate,
      trialDays: 0,
      trialEndDate: null,
      signedUpWithReferral: !!referralId,
      membershipEarnings: {
        referrals: [],
        ratingsGiven: [],
        monthsEarned: 0,
        currentEarnedMembershipEndDate: null,
      },
    };

    setUser(newUser);
    setIsFirstTimeUser(true);
    setActiveTab("verification");
    navigate("/verify", { replace: true });

    // If referral code exists, store it for later completion
    if (referralId && typeof window !== 'undefined') {
      localStorage.setItem('pendingReferralId', referralId);
    }
  };

  const handleVerificationSuccess = () => {
    const updatedUser = { ...user, verified: true };
    
    // Check for pending referral and complete it
    const pendingReferralId = localStorage.getItem('pendingReferralId');
    if (pendingReferralId && user) {
      // For now, we'll just clear the pending referral
      // In production, you'd update the referrer's account via API
      localStorage.removeItem('pendingReferralId');
    }
    
    setUser(updatedUser);
    setIsFirstTimeUser(true);
    setIsInTutorialFlow(true);
    setActiveTab("profile");
    // Must match the tab, not just leave /verify. "/" maps to services, and the
    // URL → activeTab sync would drag the member there, away from the getting
    // started card and onto a screen whose navigation is hidden during signup.
    navigate(tabToPath("profile"), { replace: true });
  };

  const handleWelcomeContinue = () => {
    setActiveTab("profile");
  };

  const handleOnboardingComplete = () => {
    setIsFirstTimeUser(false);
    setActiveTab("profile");
  };

  // Finishing the getting-started card drops the member into the app itself.
  // Clearing the tutorial flow is what lets the quick access panel appear.
  const handleFinishGettingStarted = () => {
    setIsFirstTimeUser(false);
    setIsInTutorialFlow(false);
    setActiveTab("services");
  };

  const handleSignOut = async () => {
    // Drop the push subscription first so a phone that just signed out doesn't
    // keep ringing with the previous member's notifications.
    unsubscribeFromPush().catch(() => {});
    await authApi.logout();
    setUser(null);
    setActiveTab("sign-in");
    setIsFirstTimeUser(false);
    navigate("/sign-in", { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sessionResult = await authApi.getSession();
      if (cancelled) return;

      if (!sessionResult.success || !sessionResult.data) {
        setAuthBootstrapped(true);
        return;
      }

      const meResult = await authApi.getMe();
      if (!cancelled && meResult.success) {
        handleSignIn(profileToUserData(meResult.data), { redirect: false });
        // Return the user to the page they refreshed on rather than a fixed
        // landing screen. Detail and one-off screens resolve to a parent instead,
        // since the item they were showing didn't survive the reload.
        setActiveTab(pathToRestoredTab(bootPathRef.current) || "services");
      } else if (!cancelled) {
        await authApi.logout();
      }
      if (!cancelled) setAuthBootstrapped(true);
    })();
    return () => {
      cancelled = true;
    };
    // Run once on app boot. handleSignIn intentionally not a dependency because
    // it mutates routing state and would re-run session hydration on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate after sign-in once user state has propagated (prevents white screen race condition)
  useEffect(() => {
    if (pendingSignInNavigation && user) {
      setPendingSignInNavigation(false);
      setActiveTab("services");
      navigate("/", { replace: true });
    }
  }, [pendingSignInNavigation, user, navigate, setActiveTab]);

  useEffect(() => {
    if (user && ADMIN_TABS.has(activeTab) && !isStaffUser) {
      setActiveTab("services");
      navigate("/", { replace: true });
    }
  }, [activeTab, isStaffUser, navigate, setActiveTab, user]);

  const handleItemPress = (item) => {
    setSelectedItem(item);

    // Navigate based on listing type (the Local/Pro chip is the source of truth)
    //   Job Board posts (any chip) -> requester's ProfileScreen (people seeking help)
    //   Local listing              -> owner's ProfileScreen
    //   Pro listing (Services)     -> ListingDetailsScreen
    //   Anything without a listingType (events, sponsored ads, etc.) -> ListingDetailsScreen
    const isFromJobBoard = item.source === "job-board";
    const isLocalListing = item.listingType === "local";
    const isProListing = item.listingType === "pro";

    if (isProListing) {
      console.log("[App.js] handleItemPress - Pro listing:", {
        source: item.source,
        isFromJobBoard,
        willShowProfile: isLocalListing || isFromJobBoard,
      });
    }

    if (isFromJobBoard || isLocalListing) {
      const ownerUserId = item.userId || item.user_id;
      const currentUserId = user?.id;
      const isOwnListing =
        ownerUserId &&
        currentUserId &&
        String(ownerUserId) === String(currentUserId);
      // Own listing (job ad or local service): show full profile with edit controls; others': read-only
      setIsProfileReadOnly(!isOwnListing);
      // When opening a profile from the Job Board, scroll down to the Job Ads section
      // so the user lands on the ad they just tapped. Set this BEFORE setActiveTab so
      // ProfileScreen mounts with the flag already true (React batches these updates),
      // which lets its onLayout/useLayoutEffect take the "skip scroll-to-top" branch.
      if (isFromJobBoard) {
        console.log("[App] Setting scrollToJobBoard=true for job-board item press");
        setScrollToJobBoard(true);
      } else if (isLocalListing) {
        // Local service tapped from the Services screen → land on the My Services
        // section of the provider's profile.
        console.log("[App] Setting scrollToServices=true for local listing press");
        setScrollToServices(true);
      }
      previousTabRef.current = activeTab;
      setActiveTab("profile");
      // Clear scroll flags after a brief delay to allow ProfileScreen to read them first
      setTimeout(() => {
        setScrollToJobBoard(false);
        setScrollToServices(false);
      }, 100);
    } else {
      previousTabRef.current = activeTab;
      setIsProfileReadOnly(false);
      setActiveTab("item-details");
    }
  };

  const handleOrganizerProfilePress = (organizerData) => {
    const originalEventItem = organizerData.originalEventItem || selectedItem;
    const originalBusinessItem = organizerData.originalBusinessItem || selectedItem;
    const isBusinessOwner = !!organizerData.originalBusinessItem;
    const ownerProfileData = getOrganizerProfile(organizerData.id);

    const organizerProfileItem = {
      id: organizerData.id,
      providerName: organizerData.name,
      name: organizerData.name,
      title: organizerData.name,
      description: isBusinessOwner
        ? `Owner of ${organizerData.providerName || "Business"}`
        : `Organiser of ${organizerData.organizer}`,
      rating: organizerData.rating,
      reviews: organizerData.reviews,
      reviewCount: organizerData.reviews,
      exchangeType: ownerProfileData.exchangeType || "barter",
      isOrganizer: !isBusinessOwner,
      originalEventItem,
      originalBusinessItem,
      ...ownerProfileData,
    };
    setSelectedItem(organizerProfileItem);
    setIsProfileReadOnly(true);
    // Clear scroll flags for direct navigation from listing details
    setScrollToJobBoard(false);
    setScrollToServices(false);
    setActiveTab("profile");
  };

  const handleMessageSent = (message, item) => {
    // Message sent to seller in development
    // In real app, this would save to messages or notifications
  };

  const handleNavigateToMessageDetails = (message, deleteCallback = null) => {
    setSelectedMessage(message);
    setDeleteMessageCallback(deleteCallback);
    setActiveTab("message-details");
  };

  const handleBackFromMessageDetails = () => {
    setSelectedMessage(null);
    setActiveTab("messages");
  };

  const handleMessagePartnerProfilePress = (conversation) => {
    const userId =
      conversation?.contactId ||
      conversation?.partnerId ||
      (String(conversation?.id || "").startsWith("contact-")
        ? null
        : conversation?.id);
    if (!userId) return;

    const isOwn = user?.id && String(userId) === String(user.id);
    setSelectedItem({
      id: userId,
      userId,
      user_id: userId,
      ownerId: userId,
      providerName: conversation.name,
      name: conversation.name,
      title: conversation.name,
      image: conversation.avatar,
      profileImage: conversation.avatar,
    });
    setIsProfileReadOnly(!isOwn);
    previousTabRef.current = "message-details";
    setActiveTab("profile");
  };

  const handleContactProfilePress = (contact) => {
    const userId = contact.id;
    if (!userId) return;

    const isOwn =
      user?.id && String(userId) === String(user.id);

    setSelectedItem({
      id: userId,
      userId,
      user_id: userId,
      ownerId: userId,
      providerName: contact.businessName || contact.name,
      name: contact.name,
      title: contact.businessName || contact.name,
      location: contact.location,
      email: contact.email,
      phone: contact.phone || contact.mobilePhone,
      image: contact.avatar,
      profileImage: contact.avatar,
    });
    setIsProfileReadOnly(!isOwn);
    previousTabRef.current = "contacts";
    setActiveTab("profile");
  };

  // Handle navigating to messages from contacts
  const handleNavigateToMessageFromContact = (contact) => {
    console.log("handleNavigateToMessageFromContact called with contact:", contact);
    
    // Create message object from contact data
    const messageId = `contact-${contact.id}`;
    const messageName = contact.businessName || contact.name;
    
    const message = {
      id: messageId,
      name: messageName,
      lastMessage: "", // Empty for new conversation
      time: "now",
      unread: 0,
      avatar: null,
      online: false,
      context: contact.type === "business" ? `${messageName} (Business)` : `${messageName} (Member)`,
      // Store contact reference to indicate this is a new conversation
      contactId: contact.id,
      contactType: contact.type,
    };
    
    console.log("Created message object:", message);
    
    handleNavigateToMessageDetails(message, null);
  };

  const handleSendMessage = (messageText) => {
    // Message sent in development
    // In real app, this would send the message and update the conversation
  };

  const handleBackFromFeedback = () => {
    setFeedbackInitialData({});
    setActiveTab(previousTabRef.current || "services");
  };

  const handleNavigateToReport = (itemTitle, itemId) => {
    setFeedbackInitialData({
      feedbackType: "content",
      title: `Report: ${itemTitle}`,
      description: `I would like to report this listing (ID: ${itemId}) because: `,
    });
    setActiveTab("feedback");
  };

  const handleNavigateToPendingItems = (filter = "all") => {
    setPendingItemsFilter(filter);
    previousTabRef.current = activeTab;
    setActiveTab("pending-items");
  };

  const openProfileFromNotification = (userId, name) => {
    if (!userId) return false;
    const isOwn = user?.id && String(userId) === String(user.id);
    setSelectedItem({
      id: userId,
      userId,
      user_id: userId,
      ownerId: userId,
      name: name || "Member",
      providerName: name || "Member",
      title: name || "Member",
    });
    setIsProfileReadOnly(!isOwn);
    previousTabRef.current = "notifications";
    setActiveTab("profile");
    return true;
  };

  const handleNotificationOpen = async (notification) => {
    const type = notification?.type;
    const entityId = notification?.relatedEntityId;
    const entityType = notification?.relatedEntityType;

    if (type === "new_feedback" && entityId) {
      const result = await messagesApi.getNotificationConversation(entityId);
      if (!result.success || !result.data) return false;
      handleNavigateToMessageDetails(result.data);
      return true;
    }
    if (type === "new_service_local" || type === "new_service_pro") {
      handleNavigateToPendingItems("services");
      return true;
    }
    if (type === "new_job_ad") {
      handleNavigateToPendingItems("job-postings");
      return true;
    }
    if (type === "listing_flagged") {
      previousTabRef.current = "notifications";
      setActiveTab("flagged-listings");
      return true;
    }
    if (type === "daily_planning_reminder" || type === "task_due") {
      setMyListOpenTab("todo");
      previousTabRef.current = "notifications";
      setActiveTab("my-list");
      return true;
    }
    if (
      type === "birthday_upcoming" ||
      type === "birthday_today" ||
      type === "reminder_upcoming" ||
      type === "reminder_today"
    ) {
      setMyListOpenTab("birthdays");
      previousTabRef.current = "notifications";
      setActiveTab("my-list");
      return true;
    }
    if (type === "contact_added" && entityId) {
      const nameFromMessage = String(notification.message || "")
        .replace(/\s+added you to their contacts\.?$/i, "")
        .trim();
      const name =
        nameFromMessage && nameFromMessage.toLowerCase() !== "someone"
          ? nameFromMessage
          : undefined;
      return openProfileFromNotification(entityId, name);
    }
    if (
      (type === "listing_liked" ||
        type === "listing_check_closed" ||
        type === "listing_check_wrong_number") &&
      entityId
    ) {
      const result = await servicesApi.get(entityId);
      if (!result.success || !result.data) return false;
      handleItemPress(result.data);
      return true;
    }
    if ((type === "new_review" || type === "review_reply") && entityId) {
      const result = await reviewsApi.get(entityId);
      const review = result.data;
      if (!result.success || !review) return false;
      if (review.subject_type === "user") {
        return openProfileFromNotification(review.subject_id);
      }
      if (review.subject_type === "service") {
        const listing = await servicesApi.get(review.subject_id);
        if (!listing.success || !listing.data) return false;
        handleItemPress(listing.data);
        return true;
      }
      if (review.subject_type === "event") {
        const ev = await eventsApi.get(review.subject_id);
        if (!ev.success || !ev.data) return false;
        handleItemPress({ ...ev.data, eventType: ev.data.eventType || ev.data.event_type });
        return true;
      }
      if (review.subject_type === "job" || review.subject_type === "job_posting") {
        const job = await jobsApi.get(review.subject_id);
        if (!job.success || !job.data) return false;
        handleItemPress({ ...job.data, source: "job-board" });
        return true;
      }
    }
    if (entityType === "service" && entityId) {
      const listing = await servicesApi.get(entityId);
      if (!listing.success || !listing.data) return false;
      handleItemPress(listing.data);
      return true;
    }
    return false;
  };

  // Route a push tap into the same screen the notifications list would.
  //
  // Two ways in: the service worker either posts a message to an already-open
  // tab, or opens a fresh one with ?openNotification=<id> in the URL. We look
  // the notification up (also marks it read as a side effect) and hand it to
  // the same handler the notifications list uses, so a tap and a list-click
  // land in exactly the same place.
  const openNotificationById = React.useCallback(
    async (id) => {
      if (!id || !user) return;
      const result = await notificationsApi.get(id);
      if (!result.success || !result.data) return;
      const row = result.data;
      // handleNotificationOpen expects camelCase fields on the object.
      await handleNotificationOpen({
        id: row.id,
        type: row.type,
        title: row.title,
        message: row.message,
        relatedEntityType: row.related_entity_type,
        relatedEntityId: row.related_entity_id,
      });
      if (!row.is_read) {
        notificationsApi.markRead(id).catch(() => {});
        adjustUnreadNotificationCount(-1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  // On a cold start from a push tap, App.js loads with ?openNotification=<id>
  // in the URL. Wait for the user to be bootstrapped before opening.
  useEffect(() => {
    if (!authBootstrapped || !user) return;
    const params = new URLSearchParams(location.search);
    const id = params.get("openNotification");
    if (!id) return;
    openNotificationById(id);
    // Strip the query string so a page refresh doesn't reopen the notification.
    const cleanSearch = new URLSearchParams(location.search);
    cleanSearch.delete("openNotification");
    const nextSearch = cleanSearch.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true },
    );
  }, [authBootstrapped, user, location.search, location.pathname, navigate, openNotificationById]);

  // When the PWA is already open, the service worker sends this instead of
  // opening a new tab. Same routing.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "openNotification" && event.data.id) {
        openNotificationById(event.data.id);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [openNotificationById]);

  const handleNavigateToPersonalProfile = () => {
    previousTabRef.current = activeTab;
    setActiveTab("personal-profile");
  };

  const handleNavigateToTradingProfile = (tab = "services") => {
    previousTabRef.current = activeTab;
    setActiveTab("trading-profile");
    setTradingProfileInitialTab(tab);
  };

  const handleNavigateToOpenTo = () => {
    previousTabRef.current = activeTab;
    setActiveTab("open-to");
  };

  const handleOpenToSaved = (preferences) => {
    // Optimistically update UI, then persist to trading_profiles so the
    // preference survives reload and follows the user across devices.
    setOpenToPreferences(preferences);
    setActiveTab(previousTabRef.current || "profile");
    tradingProfilesApi.saveExchangePreferences(preferences).then((result) => {
      if (!result.success) {
        console.error("Failed to save Open To preferences:", result.error);
      }
    });
  };

  const handleNavigateToReview = (serviceProvider) => {
    setSelectedServiceProvider(serviceProvider);
    previousTabRef.current = activeTab;
    setActiveTab("review");
  };

  const handleNavigateToBarteringGuide = () => {
    previousTabRef.current = activeTab;
    setActiveTab("bartering-guide");
  };

  const handleBrowsePotentialMatches = (targetScreen = "services") => {
    setActiveTab(targetScreen);
  };

  // The photo editor saves straight to the database, but every header and
  // message row reads the avatar from this in-memory user. Without telling it,
  // the new photo only appeared on the profile screen that uploaded it and
  // everywhere else kept the old one until a full reload.
  const handleAvatarUpdated = (url) => {
    if (!url) return;
    setUser((prev) =>
      prev ? { ...prev, profileImage: url, profileImageUrl: url } : prev,
    );
    // Nudge the screens that read the avatar back out of the DB.
    setProfileRefreshKey((k) => k + 1);
  };

  // Callback for when personal profile is saved
  const handlePersonalProfileSaved = (profileData = {}) => {
    // The DB row returned from the save is authoritative; strip it out of the
    // data we splat onto the user object and use it to normalise canonical fields.
    const { savedRow, ...formFields } = profileData;

    // Update the user profile with the new data
    setUser((prev) => {
      const dbName = savedRow
        ? `${savedRow.first_name || ""} ${savedRow.last_name || ""}`.trim()
        : "";
      const updated = {
        ...prev,
        ...formFields,
        name: dbName || formFields.name || prev?.name,
        first_name: savedRow?.first_name ?? prev?.first_name,
        last_name: savedRow?.last_name ?? prev?.last_name,
        // Prefer the value the DB actually stored (covers server-side coercion).
        profileImage:
          savedRow?.profile_image_url ?? formFields.profileImage ?? prev?.profileImage ?? null,
        profileImageUrl:
          savedRow?.profile_image_url ?? prev?.profileImageUrl ?? null,
      };

      // Cache for fast reload — stamped with the owner id so it can never bleed
      // into a different account on the next sign-in.
      try {
        localStorage.setItem(
          "personalProfile",
          JSON.stringify({ ...formFields, __ownerId: prev?.id || null })
        );
      } catch (e) {
        console.error("Failed to persist personal profile:", e);
      }
      return updated;
    });
    setPersonalProfileComplete(true);
    // Refresh any data hooks (e.g. ProfileScreen) reading from the DB.
    setProfileRefreshKey((k) => k + 1);

    // During signup flow, navigate directly back to profile screen to complete trading profile
    // Otherwise, show success screen for regular profile editing
    if (isFirstTimeUser) {
      setActiveTab("profile");
    } else {
    // Show success screen with appropriate message
    setSuccessData({
      editMode: true,
      type: "profile",
      message: {
        title: "Personal Profile Updated",
        message: "Your personal information has been saved successfully."
      }
    });
    setActiveTab("service-success");
    }
  };

  // Callback for when trading profile is saved (payload.savedKind: "services" | "jobs")
  const handleTradingProfileSaved = (payload = {}) => {
    setTradingProfileComplete(true);
    setProfileRefreshKey((k) => k + 1);
    setJobsListRefreshKey((k) => k + 1);

    const isJobAd = payload?.savedKind === "jobs";
    const successTitle = isJobAd ? "Job Ad Updated" : "Services Updated";
    const successBody = isJobAd
      ? "Your job ad has been saved successfully."
      : "Your services have been saved successfully.";

    // During signup flow, navigate directly back to profile screen to complete onboarding
    // Otherwise, show success screen for regular profile editing
    if (isFirstTimeUser) {
      setActiveTab("profile");
    } else {
      setSuccessData({
        editMode: true,
        type: "trading-profile",
        message: {
          title: successTitle,
          message: successBody,
        },
      });
      setActiveTab("service-success");
    }
  };

  const handleAddToPendingQueue = (itemData, contentType) => {
    const newPendingItem = {
      id: Date.now(), // Simple ID generation
      type: contentType,
      title: itemData.title,
      price: itemData.price ? `$${itemData.price}` : "Price on request",
      seller: (user && user.name) || "Unknown User",
      location: itemData.location,
      dateSubmitted: new Date().toISOString().split("T")[0], // YYYY-MM-DD format
      image:
        itemData.images && itemData.images.length > 0
          ? itemData.images[0].uri
          : "https://via.placeholder.com/300x200/E5E7EB/9CA3AF?text=No+Image",
      category: itemData.selectedCategory || "Uncategorized",
      description: itemData.description,
    };

    setPendingItemsData((prev) => [...prev, newPendingItem]);
    console.log("Added to pending queue:", newPendingItem);
  };

  // Open the service editor for a listing the current user owns (e.g. after a
  // claim is approved). Maps the transformed listing into the exact shape the
  // AddServiceScreen edit mode expects: category slugs and an array exchangeType.
  // Getting these shapes right is what prevents an edit-save from wiping the
  // listing's categories (the junction tables are now replace-synced on update).
  const handleEditService = (item) => {
    if (!item) return;
    const categorySlugs =
      Array.isArray(item.categorySlugs) && item.categorySlugs.length
        ? item.categorySlugs
        : item.category
          ? [item.category]
          : [];
    const editData = {
      id: item.id,
      title: item.title || "",
      description: item.description || "",
      abn: item.abn || "",
      businessAddress: item.businessAddress || "",
      landline: item.landline || "",
      mobile: item.mobile || item.mobilePhone || "",
      email: item.email || "",
      openingHours: Array.isArray(item.openingHours) ? item.openingHours : null,
      locations: Array.isArray(item.locations)
        ? item.locations
        : [item.location].filter(Boolean),
      type: categorySlugs.map(id => ({id, description: item.serviceTypes?.find(type => type.id === id)?.description || ""})),
      exchangeType: Array.isArray(item.exchangeType)
        ? item.exchangeType
        : [item.exchangeType].filter(Boolean),
      image: Array.isArray(item.images) ? item.images[0] : item.image,
    };
    setEditItemData(editData);
    setAddItemType("services");
    previousTabRef.current = activeTab;
    setActiveTab("add-item");
  };

  // Open the review screen for a listing/provider, normalising the review
  // subject so it's saved against the correct entity. Listings reviewed from
  // detail screens are services (or events); the subject id is the listing id.
  const handleLeaveReview = (item) => {
    if (!item) return;
    const subjectType =
      item.subjectType || (item.eventType ? "event" : "service");
    setSelectedServiceProvider({
      ...item,
      subjectType,
      subjectId: item.subjectId || item.id,
      name:
        item.name ||
        item.providerName ||
        item.seller ||
        item.title ||
        "Provider",
      service: item.service || item.categoryName || item.category || "",
    });
    // Only update previousTabRef if we're not already in a detail view (item-details, profile, etc.)
    // This preserves the navigation chain so back buttons work correctly
    if (activeTab !== "item-details" && activeTab !== "profile") {
      previousTabRef.current = activeTab;
    }
    // Recorded unconditionally, because this is the screen to return to once the
    // review is submitted or abandoned.
    reviewOriginTabRef.current = activeTab;
    setActiveTab("review");
  };

  // Favorites management functions
  const handleAddToFavorites = (item) => {
    const favoriteItem = {
      id: item.id || Date.now(), // Use item ID or timestamp as fallback
      title: item.title,
      price: item.price,
      seller: item.seller || "Unknown Seller",
      location: item.location || "Unknown Location",
      image: item.image,
      category: item.category || item.type,
      dateAdded: new Date().toISOString(),
    };

    setFavorites((prev) => {
      // Check if item is already in favorites
      if (prev.some((fav) => fav.id === favoriteItem.id)) {
        return prev; // Don't add duplicates
      }
      return [...prev, favoriteItem];
    });
  };

  const handleRemoveFromFavorites = (itemId) => {
    setFavorites((prev) => prev.filter((fav) => fav.id !== itemId));
  };

  const handleToggleFavorite = (item) => {
    const isAlreadyFavorite = favorites.some(
      (fav) => fav.id === (item.id || item.title),
    );

    if (isAlreadyFavorite) {
      handleRemoveFromFavorites(item.id || item.title);
      return false; // Removed from favorites
    } else {
      handleAddToFavorites(item);
      return true; // Added to favorites
    }
  };

  const isFavorite = (item) => {
    return favorites.some((fav) => fav.id === (item.id || item.title));
  };

  const handleSubmitFeedback = (feedback, requestId) => feedbackApi.submit(feedback, requestId);

  const handleShowTerms = () => {
    setShowTerms(true);
  };

  const handleBackFromTerms = () => {
    setShowTerms(false);
  };

  const handleShowPrivacy = () => {
    setShowPrivacy(true);
  };

  const handleBackFromPrivacy = () => {
    setShowPrivacy(false);
  };

  // Function to get display name for each screen

  // Handle hamburger menu item selection
  const handleHamburgerMenuItem = (id) => {
    setShowHamburgerMenu(false);
    previousTabRef.current = activeTab;

    if (id === "icon-reference") {
      setActiveTab("icon-reference");
    } else if (id === "style-guide") {
      setActiveTab("style-guide");
    } else if (id === "profile") {
      setIsProfileReadOnly(false);
      setActiveTab("profile");
    } else if (id === "skills-needed") {
      setActiveTab("skills-needed");

    } else if (id === "settings") {
      setActiveTab("settings");
    } else if (id === "membership") {
      setActiveTab("payment");
    } else if (id === "sponsored-listings") {
      setActiveTab("sponsored-listings");
    } else if (id === "community-calendar") {
      setActiveTab("community-calendar");
    } else if (id === "feedback") {
      setActiveTab("feedback");
    } else if (id === "about") {
      setActiveTab("about");
    } else if (id === "contacts") {
      setActiveTab("contacts");
      announceToScreenReader("Opening contacts screen");
    } else if (id === "administration") {
      setActiveTab("admin-dashboard");
    } else if (id === "sign-out") {
      setShowSignOutModal(true);
    }
  };

  const renderScreen = () => {
    const path = location.pathname;

    if (!authBootstrapped && path !== "/reset-password" && !isRecoveryLink) {
      return (
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      );
    }

    // Password recovery links create a real Supabase session, which can auto-sign
    // the user in via the bootstrap effect. Always show the reset form on this path
    // (or wherever a recovery hash lands, see isRecoveryLink above) regardless of
    // login state, otherwise a valid recovery link skips straight to the logged-in
    // app and the user never gets to set a new password.
    if (path === "/reset-password" || isRecoveryLink) {
      // The recovery session may have auto-populated `user` via the bootstrap
      // effect above; clear it so the app doesn't show a stale "logged in" UI
      // backed by a session that ResetPasswordScreen already signed out of.
      const backToSignIn = () => {
        setUser(null);
        setActiveTab("sign-in");
        navigate("/sign-in", { replace: true });
      };
      return (
        <ResetPasswordScreen
          onDone={backToSignIn}
          onCancel={backToSignIn}
          onRequestNewLink={() => navigate("/forgot-password")}
        />
      );
    }

    // Show authentication screens if user is not logged in
    if (!user) {
      if (path === "/sign-up") {
        return (
          <SignUpScreen
            onSignUp={handleSignUp}
            onNavigateToSignIn={() => navigate("/sign-in")}
            onShowTerms={handleShowTerms}
            onShowPrivacy={handleShowPrivacy}
            initialInviteCode={inviteCodeFromUrl}
            onNavigateToTutorialMinimal={showDevFeatures ? () => {
              setUser({ name: "Test User", email: "test@example.com", verified: true });
              setIsInTutorialFlow(true);
              setActiveTab("tutorial-minimal");
            } : undefined}
          />
        );
      } else if (path === "/waitlist") {
        return (
          <WaitlistScreen
            onBack={() => navigate("/")}
            onNavigateToSignIn={() => navigate("/sign-in")}
            onShowPrivacy={handleShowPrivacy}
          />
        );
      } else if (path === "/forgot-password") {
        return (
          <ForgotPasswordScreen
            onBack={() => navigate("/sign-in")}
            onResetSent={(email) => {
              console.log("Password reset sent to:", email);
            }}
          />
        );
      } else if (path === "/") {
        // A stranger arriving at the bare domain gets told what this is before
        // being asked for a password. Every other path still falls through to
        // sign-in, so nothing that links into the app changes.
        return (
          <HomeScreen
            onNavigateToSignIn={() => navigate("/sign-in")}
            onNavigateToWaitlist={() => navigate("/waitlist")}
            onShowTerms={handleShowTerms}
            onShowPrivacy={handleShowPrivacy}
          />
        );
      } else {
        return (
          <SignInScreen
            onSignIn={handleSignIn}
            onNavigateToSignUp={() => navigate("/sign-up")}
            onNavigateToForgotPassword={() => navigate("/forgot-password")}
            onNavigateToOnboarding={showDevFeatures ? () => {
              setUser({ name: "Test User", email: "test@example.com", verified: true });
              setActiveTab("onboarding");
            } : undefined}
            onNavigateToWelcome={showDevFeatures ? () => {
              setUser({ name: "Test User", email: "test@example.com", verified: true });
              setActiveTab("welcome");
            } : undefined}
            onNavigateToQuickLinks={showDevFeatures ? () => {
              setUser({ name: "Test User", email: "test@example.com", verified: true });
              setActiveTab("quick-links");
            } : undefined}
          />
        );
      }
    }

    // Show verification screen if user exists but not verified
    if (user && !user.verified) {
      return (
        <VerificationScreen
          user={user}
          onVerificationSuccess={handleVerificationSuccess}
          onBack={async () => {
            // Sign out, don't just forget the user. The Supabase session created
            // at sign-up outlives this screen otherwise, and the next reload
            // hydrates it straight back into a logged-in app.
            await authApi.logout();
            setUser(null);
            navigate("/sign-up");
          }}
        />
      );
    }

    // Show welcome screen for first-time users
    if (user && user.verified && isFirstTimeUser && activeTab === "welcome") {
      return <WelcomeScreen user={user} onContinue={handleWelcomeContinue} />;
    }

    // Onboarding screen removed - users go directly to profile screen
    switch (activeTab) {
      case "services":
        return (
          <ServicesScreen
            bottomNavigationHeight={bottomNavigationInset}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            showInstallBanner={installNudge.show}
            onInstallBannerPress={() => setShowInstallGuide(true)}
            onDismissInstallBanner={installNudge.dismiss}
            trialDaysLeft={trialDaysLeft}
            showAddMenu={showAddMenu}
            onFabPress={handleFabPress}
            onServicesPress={() => setActiveTab("services")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onMessagesPress={() => setActiveTab("messages")}
            onSettingsPress={() => navigateToSettings("services")}
            onHelpSupportPress={() => navigateToHelpSupport("services")}
            onAboutPress={() => navigateToAbout("services")}
            onFeedbackPress={() => setActiveTab("feedback")}
            onSponsoredAdPress={() => {
              setActiveTab("SponsoredAd");
            }}
            onNavigateToPayment={() => setActiveTab("payment")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onAddItem={(type) => {
              if (type === "local-service") {
                handleNavigateToTradingProfile("services");
              } else {
                setEditItemData(null);
                setAddItemType(type);
                setActiveTab("add-item");
              }
            }}
            onItemPress={handleItemPress}
            initialFilters={servicesScreenFilters}
            onFiltersChange={setServicesScreenFilters}
            initialScrollY={servicesScrollY}
            onScrollYChange={setServicesScrollY}
            australianLocations={australianLocations}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            isFirstTimeUser={isFirstTimeUser}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            viewportHeight={Math.max(0, viewportHeight - bottomNavigationInset)}
            navigation={{
              navigate: (screen, params) => {
                if (screen === "SponsoredAd" && params && params.advertisement) {
                  setSponsoredAdData(params.advertisement);
                  setSponsoredAdSource("services");
                } else if (screen === "SponsoredAd") {
                  setSponsoredAdSource("add-advertising");
                } else if (params && params.advertisement) {
                  setEditItemData(params.advertisement);
                }
                setActiveTab(screen);
              }
            }}
          />
        );
      case "sponsored-listings":
        return (
          <SponsoredListingsScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onNavigateToPayment={() => setActiveTab("payment")}
            cachedData={cachedSponsoredAds}
            onDataLoaded={setCachedSponsoredAds}
            showAddMenu={showAddMenu}
            onFabPress={handleFabPress}
            onAddItem={(type) => {
              setAddItemType(type);
              setActiveTab("add-item");
            }}
            initialFilters={sponsoredListingsScreenFilters}
            onFiltersChange={setSponsoredListingsScreenFilters}
            navigation={{
              navigate: (screen, params) => {
                if (screen === "SponsoredAd" && params && params.advertisement) {
                  setSponsoredAdData(params.advertisement);
                  setSponsoredAdSource("sponsored-listings");
                } else if (screen === "SponsoredAd") {
                  setSponsoredAdSource("sponsored-listings");
                }
                setActiveTab(screen);
              },
            }}
          />
        );
      case "skills-needed":
        return (
          <SkillsNeededScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            showAddMenu={showAddMenu}
            onFabPress={handleFabPress}
            australianLocations={australianLocations}
            cachedData={cachedJobs}
            listRefreshKey={jobsListRefreshKey}
            onDataLoaded={setCachedJobs}
            onServicesPress={() => setActiveTab("services")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onMessagesPress={() => setActiveTab("messages")}
            onSettingsPress={() => navigateToSettings("skills-needed")}
            onHelpSupportPress={() => navigateToHelpSupport("skills-needed")}
            onAboutPress={() => navigateToAbout("skills-needed")}
            onFeedbackPress={() => setActiveTab("feedback")}
            onSponsoredAdPress={() => setActiveTab("SponsoredAd")}
            onNavigateToPayment={() => setActiveTab("payment")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onAddItem={(type) => {
              if (type === "add-job") {
                console.log("[App] Add Job clicked, navigating to TradingProfileScreen with scrollToJobBoardSection");
                setTradingProfileInitialTab("jobs");
                // Set scrollToJobBoardSection after a small delay to ensure TradingProfileScreen is mounted
                setTimeout(() => {
                  setScrollToJobBoardSection(true);
                  console.log("[App] scrollToJobBoardSection set to true");
                }, 100);
                setActiveTab("trading-profile");
              } else {
                setAddItemType(type);
                setActiveTab("add-item");
              }
            }}
            onItemPress={handleItemPress}
            initialFilters={skillsNeededScreenFilters}
            onFiltersChange={setSkillsNeededScreenFilters}
            navigation={{
              navigate: (screen, params) => {
                if (screen === "SponsoredAd" && params && params.advertisement) {
                  setSponsoredAdData(params.advertisement);
                  setSponsoredAdSource("services");
                } else if (screen === "SponsoredAd") {
                  setSponsoredAdSource("add-advertising");
                } else if (params && params.advertisement) {
                  setEditItemData(params.advertisement);
                }
                setActiveTab(screen);
              }
            }}
          />
        );
      case "profile":
        return (
          <ProfileScreen
            key={`${profileTutorialKey}-${isProfileReadOnly ? 'readonly' : 'edit'}-${selectedItem?.id || 'none'}-${user?.id || 'guest'}`}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            favorites={favorites}
            onRemoveFromFavorites={handleRemoveFromFavorites}
            user={user}
            onSignOut={handleSignOut}
            onAvatarUpdated={handleAvatarUpdated}
            onNavigateToServices={() => setActiveTab("services")}
            onNavigateToAddService={() => {
              setEditItemData(null);
              setAddItemType("services");
              setActiveTab("add-item");
            }}
            onNavigateToMyListings={() => setActiveTab("my-listings")}
            onNavigateToFavorites={() => { previousTabRef.current = activeTab; setActiveTab("favorites"); }}
            onNavigateToAdminDashboard={() => { previousTabRef.current = activeTab; setActiveTab("admin-dashboard"); }}
            onNavigateToPersonalProfile={handleNavigateToPersonalProfile}
            onNavigateToTradingProfile={handleNavigateToTradingProfile}
            onNavigateToOpenTo={handleNavigateToOpenTo}
            openToPreferences={openToPreferences}
            refreshKey={profileRefreshKey}
            onNavigateToBarteringGuide={handleNavigateToBarteringGuide}
            personalProfileComplete={personalProfileComplete}
            tradingProfileComplete={tradingProfileComplete}
            onPersonalProfileComplete={() => setPersonalProfileComplete(true)}
            onTradingProfileComplete={() => setTradingProfileComplete(true)}
            onboardingCompleted={
              personalProfileComplete && tradingProfileComplete
            }
            onOnboardingComplete={handleOnboardingComplete}
            onTutorialComplete={() => {
              setShowProfileTutorial(false);
              setIsInTutorialFlow(false);
            }}
            onNavigateToPayment={() => setActiveTab("payment")}
            onFinishGettingStarted={handleFinishGettingStarted}
            onLeaveReview={handleLeaveReview}
            onHamburgerMenuPress={(activeTab === "tutorial-minimal" || (!isInTutorialFlow && !isFirstTimeUser)) ? () => setShowHamburgerMenu(true) : undefined}
            onMessagesPress={(activeTab === "tutorial-minimal" || (!isInTutorialFlow && !isFirstTimeUser)) ? () => setActiveTab("messages") : undefined}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={(activeTab === "tutorial-minimal" || (!isInTutorialFlow && !isFirstTimeUser)) ? () => setActiveTab("notifications") : undefined}
            onServicesPress={() => setActiveTab("services")}
            onItemPress={handleItemPress}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            isReadOnly={isProfileReadOnly}
            communityServiceData={isProfileReadOnly ? selectedItem : null}
            onBack={isProfileReadOnly
              ? () => {
                if (selectedItem?.originalEventItem) {
                  setSelectedItem(selectedItem.originalEventItem);
                } else if (selectedItem?.originalBusinessItem) {
                  setSelectedItem(selectedItem.originalBusinessItem);
                }
                setIsProfileReadOnly(false);
                setActiveTab(previousTabRef.current || "services");
              }
              : undefined}
            isSignup={isFirstTimeUser}
            showTutorialOnMount={showProfileTutorial}
            onOpenTestimonialsModal={(data, title, name, id) => openTestimonialsModal(
              data || (isProfileReadOnly && selectedItem?.testimonials ? selectedItem.testimonials : testimonials),
              title || "Profile",
              name || "Service Provider",
              id != null ? id : (isProfileReadOnly ? selectedItem?.ownerId : user?.id)
            )}
            testimonials={isProfileReadOnly && selectedItem?.testimonials ? selectedItem.testimonials : testimonials}
            onResponseSubmit={handleResponseSubmit}
            onNavigateToAddAdvertising={() => {
              setAddItemType("advertising");
              setActiveTab("add-item");
            }}
            onNavigateToAdvertisements={(advertisements, providerName) => {
              setAdvertisementsScreenData({ advertisements, providerName });
              setActiveTab("advertisements");
            }}
            onNavigateToSponsoredAd={(listing) => {
              setSponsoredAdData(listing);
              setSponsoredAdSource("profile");
              setActiveTab("SponsoredAd");
            }}
            scrollToJobBoard={scrollToJobBoard}
            scrollToServices={scrollToServices}
            />
        );
      case "notifications":
        return (
          <NotificationsScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            onBackPress={() => setActiveTab(inboxOriginRef.current)}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            onNavigateToPayment={() => setActiveTab("payment")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onOpenNotification={handleNotificationOpen}
            onShowInstallGuide={() => setShowInstallGuide(true)}
          />
        );
      case "icon-reference":
        return (
          <IconReferenceScreen
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onBack={() => setActiveTab("admin-dashboard")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
          />
        );
      case "messages":
        return (
          <MessagesScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            onBack={() => setActiveTab(inboxOriginRef.current)}
            onNavigateToDetails={handleNavigateToMessageDetails}
            onNavigateToReview={handleNavigateToReview}
            onNavigateToServices={() => setActiveTab("services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onNavigateToPayment={() => setActiveTab("payment")}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
          />
        );
      case "community-chat":
        return (
          <CommunityChatScreen
            user={user}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
          />
        );
      case "community-calendar":
        return (
          <CommunityCalendarScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            showAddMenu={showAddMenu}
            onFabPress={handleFabPress}
            cachedData={cachedEvents}
            onDataLoaded={setCachedEvents}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            onAddItem={(type) => {
              setEditItemData(null);
              setAddItemType(type);
              setActiveTab("add-item");
            }}
            onItemPress={handleItemPress}
            australianLocations={australianLocations}
            userSubscriptionTier={user?.subscriptionTier || "free"}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "job-applications":
        return canAccessPrivateJobs ? <JobApplicationsScreen key={user.id} onBack={() => { setMyListOpenTab("birthdays"); setActiveTab("my-list"); }} /> : <View style={{padding:24}}><Text>This page is unavailable for this account.</Text></View>;
      case "settings":
        return (
          <SettingsScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onNavigateToChangePassword={() => setActiveTab("change-password")}
            onNavigateToPaymentMethods={() => setActiveTab("payment-methods")}
            onShowTerms={handleShowTerms}
            onShowPrivacy={handleShowPrivacy}
            onShowInstallGuide={() => setShowInstallGuide(true)}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "change-password":
        return <ChangePasswordScreen onBack={() => setActiveTab("settings")} />;
      case "payment-methods":
        if (!showDevFeatures) return <PaymentsUnavailable />;
        return <PaymentMethodsScreen
          onBack={() => setActiveTab("settings")}
          paymentMethodView={paymentMethodView}
          onPaymentMethodViewChange={setPaymentMethodView}
          selectedExpiryDate={selectedExpiryDate}
          onNavigate={(screen, callback) => {
            if (screen === "date-picker" && callback) {
              datePickerCallbackRef.current = callback;
            }
            setActiveTab(screen);
          }}
        />;
      case "date-picker":
        if (!showDevFeatures) return <PaymentsUnavailable />;
        return <DatePickerScreen
          onBack={() => setActiveTab("payment-methods")}
          onDateSelected={(date) => {
            // Store the date in App state
            setSelectedExpiryDate(date);
            // Also call the callback if it exists
            if (datePickerCallbackRef.current) {
              datePickerCallbackRef.current(date);
            }
          }}
        />;
      case "my-listings":
        return (
          <MyListingsScreen
            onBack={() => setActiveTab("profile")}
            onItemPress={handleItemPress}
          />
        );
      case "favorites":
        return (
          <FavoritesScreen
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onItemPress={handleItemPress}
            favorites={favorites}
            onRemoveFromFavorites={handleRemoveFromFavorites}
          />
        );
      case "admin-dashboard":
        return (
          <AdminDashboardScreen
            viewState={adminHubView?.userId === user?.id ? adminHubView : undefined}
            onViewStateChange={(next) => setAdminHubView({ ...next, userId: user?.id })}
            onBack={() => setActiveTab(previousTabRef.current || "profile")}
            onNavigateToPendingItems={handleNavigateToPendingItems}
            onNavigateToAnalytics={() => setActiveTab("analytics")}
            onNavigateToUserManagement={() => setActiveTab("user-management")}
            onNavigateToFlaggedReviews={() => setActiveTab("flagged-reviews")}
            onNavigateToReportedListings={() => setActiveTab("flagged-listings")}
            onNavigateToTechStack={() => setActiveTab("tech-stack")}
            onNavigateToInviteCodes={() => setActiveTab("invite-codes")}
            onNavigateToWaitlist={() => setActiveTab("waitlist-admin")}
            onNavigateToPhoneChecks={() => setActiveTab("phone-checks")}
            onNavigateToFeedback={() => setActiveTab("admin-feedback")}
            onServicesPress={() => setActiveTab("services")}
            onIconReferencePress={() => {
              previousTabRef.current = activeTab;
              setActiveTab("icon-reference");
            }}
            onUxStyleGuidePress={() => {
              previousTabRef.current = activeTab;
              setActiveTab("ux-style-guide");
            }}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "admin-feedback":
        return <AdminFeedbackScreen user={user} onBack={() => setActiveTab("admin-dashboard")}
          onServicesPress={() => setActiveTab("services")}
          onMessagesPress={() => setActiveTab("messages")}
          onNotificationsPress={() => setActiveTab("notifications")}
          unreadNotifications={unreadNotifications} unreadMessages={unreadMessages}
          onOpenConversation={item => handleNavigateToMessageDetails({id: item.author_id, partnerId: item.author_id,
            name: `${item.author?.first_name || "Member"} ${item.author?.last_name || ""}`.trim()})} />;
      case "ux-style-guide":
        return (
          <UxStyleGuideScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "user-management":
        return (
          <UserManagementScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onUserPress={(user) => {
              setSelectedUser(user);
              setActiveTab("user-details");
            }}
          />
        );
      case "user-details":
        return (
          <UserDetailsScreen
            user={selectedUser}
            onBack={() => setActiveTab("user-management")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={(user) => {
              // Convert user to selectedItem format for ProfileScreen
              const profileItem = {
                id: user.id,
                name: user.name,
                email: user.email,
                phone: user.mobilePhone,
                serviceTypes: user.serviceTypes,
                reputation: user.reputation,
                exchanges: user.exchanges,
                listingsCreated: user.listingsCreated,
                messagesSent: user.messagesSent,
                messagesReceived: user.messagesReceived,
                avgTimeOnApp: user.avgTimeOnApp,
                joinDate: user.joinDate,
                lastActive: user.lastActive,
                isOnline: user.isOnline,
              };
              setSelectedItem(profileItem);
              setIsProfileReadOnly(true);
              setActiveTab("profile");
            }}
            onUserDeactivate={handleUserDeactivate}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "analytics":
        return (
          <AnalyticsScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "pending-items":
        return (
          <PendingItemsScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            initialFilter={pendingItemsFilter}
            pendingItems={pendingItemsData}
            onUpdatePendingItems={setPendingItemsData}
          />
        );
      case "waitlist-admin":
        return (
          <WaitlistAdminScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "phone-checks":
        return (
          <PhoneChecksScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "flagged-reviews":
        return (
          <FlaggedReviewsScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "flagged-listings":
        return (
          <FlaggedListingsScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "tech-stack":
        return (
          <TechStackScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "invite-codes":
        return (
          <InviteCodesScreen
            onBack={() => setActiveTab("admin-dashboard")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            user={user}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "personal-profile":
        return (
          <PersonalProfileScreen
            onBack={() => setActiveTab(previousTabRef.current || "profile")}
            onProfileSaved={handlePersonalProfileSaved}
            onAvatarUpdated={handleAvatarUpdated}
            initialData={user}
            onOpenLocationModal={openLocationModal}
          />
        );
      case "quick-links":
        return (
          <QuickLinksScreen
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onNavigateToTradingProfile={() => {
              setActiveTab("trading-profile");
            }}
            onNavigateToOnboarding={() => {
              setActiveTab("onboarding");
            }}
            onNavigateToUserManagement={() => {
              setActiveTab("user-management");
            }}
            onNavigateToTutorialMinimal={showDevFeatures ? () => {
              setUser({ name: "Test User", email: "test@example.com", verified: true });
              setIsInTutorialFlow(true);
              setActiveTab("tutorial-minimal");
            } : undefined}
            onNavigateToPendingItems={() => {
              setActiveTab("pending-items");
            }}
            onNavigateToJobPostings={() => {
              handleNavigateToPendingItems("job-postings");
            }}
            onNavigateToAnalytics={() => {
              setActiveTab("analytics");
            }}
          />
        );
      case "trading-profile":
        return (
          <TradingProfileScreen
            onBack={() => setActiveTab(previousTabRef.current || "profile")}
            onBrowseMatches={handleBrowsePotentialMatches}
            onNavigateToBarteringGuide={handleNavigateToBarteringGuide}
            onProfileSaved={handleTradingProfileSaved}
            onNavigateToOpenTo={handleNavigateToOpenTo}
            scrollToJobBoardSection={scrollToJobBoardSection}
            initialTab={tradingProfileInitialTab}
            user={user}
          />
        );
      case "open-to":
        return (
          <OpenToScreen
            onBack={() => setActiveTab(previousTabRef.current || "profile")}
            onSaved={handleOpenToSaved}
            initialPreferences={openToPreferences}
          />
        );
      case "bartering-guide":
        return (
          <BarteringGuideScreen
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onNavigateToTradingProfile={handleNavigateToTradingProfile}
          />
        );
      case "review":
        return (
          <ReviewScreen
            serviceProvider={selectedServiceProvider}
            onBack={() =>
              setActiveTab(
                reviewOriginTabRef.current || previousTabRef.current || "services",
              )
            }
            onSubmitReview={(reviewData) => {
              console.log("Review submitted:", reviewData);
              if (reviewData?.isUpdate) return;
              // Track rating for membership earnings
              if (user && user.id) {
                const updatedEarnings = {
                  ...user.membershipEarnings,
                  ratingsGiven: [
                    ...(user.membershipEarnings?.ratingsGiven || []),
                    {
                      providerId: reviewData.providerId,
                      date: new Date().toISOString(),
                    },
                  ],
                };
                const updatedUser = {
                  ...user,
                  membershipEarnings: updatedEarnings,
                };
                setUser(updatedUser);
              }
            }}
            onSuccess={(successData) => {
              setProfileRefreshKey((k) => k + 1);
              setSuccessData(successData);
              // Back to the listing or profile just reviewed, so the member sees
              // their review land. previousTabRef points at whatever came before
              // that detail view, which is why this used to drop them on Services.
              setActiveTab(
                reviewOriginTabRef.current || previousTabRef.current || "services",
              );
            }}
            user={user}
          />
        );
      case "style-guide":
        return (
          <StyleGuideScreen
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
          />
        );
      case "about":
        return (
          <AboutScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            onShowPrivacy={() => { previousTabRef.current = activeTab; setActiveTab("privacy"); }}
            onShowTerms={() => { previousTabRef.current = activeTab; setActiveTab("terms"); }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "item-details":
        return (
          <ListingDetailsScreen
            item={selectedItem}
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onMessageSent={handleMessageSent}
            onReport={(item) => handleNavigateToReport(item.title || item.providerName, item.id)}
            onToggleFavorite={(item) => handleToggleFavorite(item)}
            isFavorite={selectedItem ? isFavorite(selectedItem) : false}
            onLeaveReview={handleLeaveReview}
            onOpenTestimonialsModal={openTestimonialsModal}
            onNavigateToBarteringGuide={() => setActiveTab("bartering-guide")}
            onEdit={handleEditService}
            onNavigateToAddService={() => {
              setEditItemData(null);
              setAddItemType("services");
              setActiveTab("add-item");
            }}
            onNavigateToClaimBusiness={(item) => {
              setSelectedItem(item);
              setActiveTab("claim-business");
            }}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            onNavigateToPayment={() => setActiveTab("payment")}
            onOrganizerProfilePress={handleOrganizerProfilePress}
            user={user}
            onNavigateToLocations={(locations, providerName) => {
              setLocationsScreenData({ locations, providerName });
              setActiveTab("locations");
            }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "locations":
        return (
          <LocationsScreen
            locations={locationsScreenData?.locations || []}
            providerName={locationsScreenData?.providerName || ""}
            onBack={() => setActiveTab("item-details")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "advertisements":
        return (
          <AdvertisementsScreen
            advertisements={advertisementsScreenData?.advertisements || []}
            providerName={advertisementsScreenData?.providerName || ""}
            onBack={() => setActiveTab("item-details")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onItemPress={(listing) => {
              // Navigate to SponsoredAdScreen
              setSponsoredAdData(listing);
              setSponsoredAdSource("advertisements");
              setActiveTab("SponsoredAd");
            }}
          />
        );
      case "claim-business":
        return (
          <ClaimBusinessScreen
            route={{ params: { item: selectedItem } }}
            navigation={{
              navigate: () => {},
              goBack: () => setActiveTab("services"),
            }}
            onSuccess={(successData) => {
              setSuccessData(successData);
              setActiveTab("service-success");
            }}
          />
        );
      case "message-details":
        return selectedMessage ? (
          <MessageDetailsScreen
            message={selectedMessage}
            onBack={handleBackFromMessageDetails}
            onSendMessage={handleSendMessage}
            onPartnerProfilePress={handleMessagePartnerProfilePress}
          />
        ) : (
          <MessagesScreen
            onBack={() => setActiveTab("services")}
            onNavigateToDetails={handleNavigateToMessageDetails}
            onNavigateToServices={() => setActiveTab("services")}
            onNavigateToPayment={() => setActiveTab("payment")}
          />
        );
      case "feedback":
        return (
          <FeedbackScreen
            user={user}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            onBack={handleBackFromFeedback}
            onSubmitFeedback={handleSubmitFeedback}
            onSuccess={(successData) => {
              setSuccessData(successData);
              setActiveTab("service-success");
            }}
            onNavigateToPayment={() => setActiveTab("payment")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            initialFeedbackType={feedbackInitialData.feedbackType}
            initialTitle={feedbackInitialData.title}
            initialDescription={feedbackInitialData.description}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "contacts":
        return (
          <ContactsScreen
            user={user}
            onBack={() => setActiveTab("services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onServicesPress={() => setActiveTab("services")}
            onNavigateToMessage={handleNavigateToMessageFromContact}
            onContactPress={handleContactProfilePress}
            onQRCodePress={() => setActiveTab("qr-code")}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "qr-code":
        return (
          <QRCodeScreen
            user={user}
            onBack={() => setActiveTab("services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "my-list":
        return (
          <MyListScreen
            canAccessPrivateJobs={canAccessPrivateJobs}
            onNavigateToJobApplications={() => {
              if(canAccessPrivateJobs) setActiveTab("job-applications");
            }}
            onActionMenuChange={setOrganiserActionsVisible}
            bottomNavigationHeight={bottomNavigationInset}
            onBack={() => setActiveTab("services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onServicesPress={() => setActiveTab("services")}
            onItemPress={handleItemPress}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            viewportHeight={Math.max(0, viewportHeight - bottomNavigationInset)}
            onMapExpandedChange={setMapExpanded}
            initialTab={myListOpenTab}
            onInitialTabConsumed={() => setMyListOpenTab(null)}
          />
        );
      case "terms":
        return (
          <TermsOfService onBack={() => setActiveTab(previousTabRef.current || "services")} />
        );
      case "privacy":
        return (
          <PrivacyPolicy onBack={() => setActiveTab(previousTabRef.current || "services")} />
        );
      case "payment":
        if (!showDevFeatures && !canPreviewSubscription) return <PaymentsUnavailable />;
        return (
          <PaymentScreen
            onBack={() => setActiveTab(previousTabRef.current || "services")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onSuccess={(successData) => {
              setSuccessData(successData);
              setActiveTab("service-success");
            }}
            user={user}
            onUserUpdate={(updatedUser) => setUser(updatedUser)}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "add-item":
        switch (addItemType) {
          case "services":
            return (
              <AddServiceScreen
                onBackPress={() => {
                  setEditItemData(null);
                  setActiveTab("services");
                }}
                onSubmitItem={(itemData) =>
                  handleAddToPendingQueue(itemData, "services")
                }
                onSuccess={(successData) => {
                  setEditItemData(null);
                  setProfileRefreshKey((k) => k + 1);
                  setSuccessData(successData);
                  setActiveTab("service-success");
                }}
                onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
                onServicesPress={() => setActiveTab("services")}
                onMessagesPress={() => setActiveTab("messages")}
                onNotificationsPress={() => setActiveTab("notifications")}
                unreadNotifications={unreadNotifications}
                unreadMessages={unreadMessages}
                showTrialBanner={shouldShowTrialBanner}
                onDismissTrialBanner={dismissTrialBanner}
                trialDaysLeft={trialDaysLeft}
                onNavigateToPayment={() => setActiveTab("payment")}
                editMode={!!editItemData}
                itemToEdit={editItemData}
                onOpenLocationModal={openLocationModal}
              />
            );
          case "event":
            return (
              <AddEventScreen
                onBackPress={() => setActiveTab("community-calendar")}
                onSuccess={(successData) => {
                  // Navigate to success screen
                  setSuccessData({
                    ...successData,
                    type: "event",
                  });
                  setActiveTab("service-success");
                }}
                onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
                onServicesPress={() => setActiveTab("services")}
                onMessagesPress={() => setActiveTab("messages")}
                onNotificationsPress={() => setActiveTab("notifications")}
                unreadNotifications={unreadNotifications}
                unreadMessages={unreadMessages}
                showTrialBanner={shouldShowTrialBanner}
                onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
                onNavigateToPayment={() => setActiveTab("payment")}
                editMode={!!editItemData}
                itemToEdit={editItemData}
                onOpenLocationModal={openLocationModal}
                userProfile={user}
              />
            );
          case "advertising":
            return (
              <AddAdvertisingScreen
              key={`advertising-${addAdvertisingKeyCounter}`}
              onBack={() => setActiveTab("services")}
              onSuccess={(successData) => {
                setSuccessData(successData);
                setActiveTab("service-success");
              }}
              onOpenLocationModal={openLocationModal}
              onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
              onServicesPress={() => setActiveTab("services")}
              onMessagesPress={() => setActiveTab("messages")}
              onNotificationsPress={() => setActiveTab("notifications")}
              unreadNotifications={unreadNotifications}
              unreadMessages={unreadMessages}
              showTrialBanner={shouldShowTrialBanner}
              onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
              onNavigateToPayment={() => setActiveTab("payment")}
              userProfileName="William The Gardener"
              userEmail="wililam@oe.com"
              userPhone="0414486876"
              userBusinesses={[
                {
                  id: 1,
                  name: "William's Gardening Services",
                  type: "Gardening",
                  location: "Bundaberg, QLD",
                  status: "Active",
                  mobilePhone: "0412 345 678",
                  email: "williams.gardening@email.com",
                },
              ]}
              hasPlusMembership={user?.subscriptionTier === "plus"}
              hasAccruedFreeMonths={(user?.accruedFreeMonths || 0) > 0}
              navigation={{
                navigate: (screen, params) => {
                  if (screen === "SponsoredAd" && params && params.advertisement) {
                    // Wrapped advertisement data from other screens
                    setSponsoredAdData(params.advertisement);
                    setSponsoredAdSource("services");
                  } else if (screen === "SponsoredAd") {
                    // Direct navigation from AddAdvertisingScreen (no params)
                    setSponsoredAdSource("add-advertising");
                  } else if (params && params.advertisement) {
                    setEditItemData(params.advertisement);
                  }
                  setActiveTab(screen);
                }
              }}
            />
            );
          default:
            return (
              <ServicesScreen
            bottomNavigationHeight={bottomNavigationInset}
                showTrialBanner={shouldShowTrialBanner}
                onDismissTrialBanner={dismissTrialBanner}
                trialDaysLeft={trialDaysLeft}
                showAddMenu={showAddMenu}
                onFabPress={handleFabPress}
                onServicesPress={() => setActiveTab("services")}
                onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
                onNotificationsPress={() => setActiveTab("notifications")}
                onMessagesPress={() => setActiveTab("messages")}
                onSettingsPress={() => navigateToSettings("services")}
                onHelpSupportPress={() => navigateToHelpSupport("services")}
                onAboutPress={() => navigateToAbout("services")}
                onFeedbackPress={() => setActiveTab("feedback")}
                onSponsoredAdPress={() => setActiveTab("SponsoredAd")}
                onNavigateToPayment={() => setActiveTab("payment")}
                onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
                onAddItem={(type) => {
                  setEditItemData(null);
                  setAddItemType(type);
                  setActiveTab("add-item");
                }}
                onItemPress={handleItemPress}
                initialFilters={servicesScreenFilters}
                onFiltersChange={setServicesScreenFilters}
                australianLocations={australianLocations}
                activeEmergencyNotice={activeEmergencyNotice}
                onDismissEmergencyNotice={handleDismissEmergencyNotice}
                onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
                isFirstTimeUser={isFirstTimeUser}
                user={user}
                navigation={{
                  navigate: (screen, params) => {
                    if (screen === "SponsoredAd" && params && params.advertisement) {
                      setSponsoredAdData(params.advertisement);
                      setSponsoredAdSource("services");
                    } else if (screen === "SponsoredAd") {
                      setSponsoredAdSource("add-advertising");
                    } else if (params && params.advertisement) {
                      setEditItemData(params.advertisement);
                    }
                    setActiveTab(screen);
                  }
                }}
              />
            );
        }
      case "SponsoredAd":
        return (
          <SponsoredAdScreen
            advertisement={sponsoredAdData}
            onBack={() => {
              const backTo = sponsoredAdSource || previousTabRef.current || "services";
              setSponsoredAdSource(null);
              setSponsoredAdData(null);
              setActiveTab(backTo);
            }}
            onMessageSent={handleMessageSent}
            onOpenTestimonialsModal={openTestimonialsModal}
            onLeaveReview={handleLeaveReview}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            showTrialBanner={shouldShowTrialBanner}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onNotificationsPress={() => setActiveTab("notifications")}
            onMessagesPress={() => setActiveTab("messages")}
            onNavigateToPayment={() => setActiveTab("payment")}
            onNavigateToLocations={(locations, providerName) => {
              setLocationsScreenData({ locations, providerName });
              setActiveTab("locations");
            }}
          />
        );
      case "onboarding":
        return (
          <OnboardingScreen
            onComplete={() => {
              setIsFirstTimeUser(false);
              setIsProfileReadOnly(false);
              setActiveTab("profile");
            }}
            onBack={() => setActiveTab("profile")}
            onNavigateToServices={() => setActiveTab("services")}
            onNavigateToProfileWithTutorial={() => {
              setIsFirstTimeUser(false);
              setIsProfileReadOnly(false);
              setActiveTab("tutorial");
            }}
            onNavigateToTutorialMinimal={() => {
              setIsFirstTimeUser(false);
              setIsInTutorialFlow(true);
              setActiveTab("tutorial-minimal");
            }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "tutorial":
        return (
          <TutorialScreen
            onComplete={() => {
              setIsInTutorialFlow(false); // Allow automatic redirection to services now
              setActiveTab("profile");
            }}
            onSkip={() => {
              setIsInTutorialFlow(false); // Allow automatic redirection to services now
              setActiveTab("profile");
            }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "tutorial-minimal":
        return (
          <TutorialScreenMinimal
            onComplete={() => {
              setIsInTutorialFlow(false);
              setActiveTab("services");
            }}
            onSkip={() => {
              setIsInTutorialFlow(false);
              setActiveTab("services");
            }}
            onExit={() => {
              setIsInTutorialFlow(false);
              setActiveTab("services");
            }}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
          />
        );
      case "service-success":
        return (
          <ServiceSuccessScreen
            editMode={successData?.editMode || false}
            successType={successData?.type || "service"}
            successMessage={successData?.message}
            onBackPress={() => {
              if (successData?.type === "profile" || successData?.type === "trading-profile") {
                setActiveTab("profile");
              } else if (successData?.type === "advertising") {
                // For advertisement creation, always go back to services
                setActiveTab("services");
              } else if (successData?.type === "event") {
                // For event creation, go back to community calendar
                setActiveTab("community-calendar");
              } else {
                setActiveTab("services");
              }
            }}
            onAddAdvertisingPress={() => {
              setAddItemType("advertising");
              setActiveTab("add-item");
            }}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onServicesPress={() => setActiveTab("services")}
            onMessagesPress={() => setActiveTab("messages")}
            onNotificationsPress={() => setActiveTab("notifications")}
            unreadNotifications={unreadNotifications}
            unreadMessages={unreadMessages}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
          />
        );
      default:
        return (
          <ServicesScreen
            bottomNavigationHeight={bottomNavigationInset}
            showTrialBanner={shouldShowTrialBanner}
            onDismissTrialBanner={dismissTrialBanner}
            trialDaysLeft={trialDaysLeft}
            showAddMenu={showAddMenu}
            onFabPress={handleFabPress}
            onServicesPress={() => setActiveTab("services")}
            onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
            onNotificationsPress={() => setActiveTab("notifications")}
            onMessagesPress={() => setActiveTab("messages")}
            onSettingsPress={() => navigateToSettings("services")}
            onHelpSupportPress={() => navigateToHelpSupport("services")}
            onAboutPress={() => navigateToAbout("services")}
            onFeedbackPress={() => setActiveTab("feedback")}
            onSponsoredAdPress={() => setActiveTab("SponsoredAd")}
            onNavigateToPayment={() => setActiveTab("payment")}
            onHamburgerMenuPress={() => setShowHamburgerMenu(true)}
            onAddItem={(type) => {
              setEditItemData(null);
              setAddItemType(type);
              setActiveTab("add-item");
            }}
            onItemPress={handleItemPress}
            initialFilters={servicesScreenFilters}
            onFiltersChange={setServicesScreenFilters}
            australianLocations={australianLocations}
            activeEmergencyNotice={activeEmergencyNotice}
            onDismissEmergencyNotice={handleDismissEmergencyNotice}
            onViewEmergencyNoticeDetails={handleViewEmergencyNoticeDetails}
            isFirstTimeUser={isFirstTimeUser}
            user={user}
            navigation={{
              navigate: (screen, params) => {
                if (screen === "SponsoredAd" && params && params.advertisement) {
                  setSponsoredAdData(params.advertisement);
                  setSponsoredAdSource("services");
                } else if (screen === "SponsoredAd") {
                  setSponsoredAdSource("add-advertising");
                } else if (params && params.advertisement) {
                  setEditItemData(params.advertisement);
                }
                setActiveTab(screen);
              }
            }}
          />
        );
      }
    };

  const bottomMenuVisible = user && user.verified && location.pathname !== "/reset-password" && !isRecoveryLink && !mapExpanded && (activeTab === "tutorial-minimal" || (!isInTutorialFlow && !isFirstTimeUser && activeTab !== "onboarding"));
  const bottomNavigationInset = bottomMenuVisible ? bottomNavHeight : 0;

  return (
    <PaperProvider
      theme={theme}
      settings={{
        icon: (props) => {
          // Return null to completely disable icon loading and prevent module references
          return null;
        },
      }}
    >
      <RootContainer style={Platform.OS === 'web' ? webStyles.container : styles.container}>
        <AppBackground />
        <ContentContainer style={Platform.OS === 'web' ? webStyles.appContentLayer : styles.appContentLayer}>
        <ContentContainer style={Platform.OS === 'web' ? Object.assign({}, webStyles.content, !user ? webStyles.contentFullScreen : {}) : [styles.content, !user && styles.contentFullScreen]}>
          <View style={{flex: 1, minHeight: 0, overflow: "hidden"}}>
          <Suspense fallback={
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 120 }}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          }>
            {renderScreen()}
          </Suspense>
          </View>
          {/* Quick Access Panel - Show when user is logged in, verified, and either past tutorial OR during tutorial-minimal */}
          {/* Hidden on the reset-password screen: a recovery link can auto-establish a
              session (see isRecoveryLink above), which would otherwise make the main nav
              flash in behind the "set new password" form before the user has really signed in. */}
          {bottomMenuVisible && <View style={{height: bottomNavigationInset, flexShrink: 0}} />}
          {bottomMenuVisible && !(activeTab === "my-list" && organiserActionsVisible) && (
            <CollapsibleQuickAccess
              persistent
              onLayout={event => setBottomNavHeight(event.nativeEvent.layout.height)}
              onServicesPress={() => setActiveTab("services")}
              onJobBoardPress={() => setActiveTab("skills-needed")}
              onOffersPress={() => setActiveTab("sponsored-listings")}
              onCalendarPress={() => setActiveTab("community-calendar")}
              onCommunityChatPress={() => setActiveTab("community-chat")}
              onProfilePress={() => { setIsProfileReadOnly(false); setActiveTab("profile"); }}
              onMyListPress={() => setActiveTab("my-list")}
              onContactsPress={() => setActiveTab("contacts")}
              onQRCodePress={() => setActiveTab("qr-code")}
              activeTab={activeTab}
              myListUnseenCount={myListUnseenCount}
            />
          )}

        </ContentContainer>

        {/* Legal Documents Overlays */}
        {showTerms && (
          <View style={styles.overlay}>
            <Suspense fallback={null}>
              <TermsOfService onBack={handleBackFromTerms} />
            </Suspense>
          </View>
        )}

        {showPrivacy && (
          <View style={styles.overlay}>
            <Suspense fallback={null}>
              <PrivacyPolicy onBack={handleBackFromPrivacy} />
            </Suspense>
          </View>
        )}


        {/* Location Selection Modal - Rendered at App level for proper overlay */}
        <Modal
          visible={showLocationModal}
          animationType="slide"
          presentationStyle="overFullScreen"
          transparent={false}
          onRequestClose={() => setShowLocationModal(false)}
          onShow={() => {
            // Auto-focus search input when modal opens
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }}
        >
          <View style={styles.locationModalContainer}>
            {/* Modal Header */}
            <View style={styles.locationModalHeader}>
              <TouchableOpacity
                onPress={() => setShowLocationModal(false)}
                style={styles.locationModalCloseButton}
                accessible={true}
                accessibilityLabel="Close location selection"
                accessibilityHint="Closes the location selection modal"
                accessibilityRole="button"
              >
                <Icon name="close" size={24} color={theme.colors.onSurface} />
              </TouchableOpacity>
              <Text style={styles.locationModalTitle}>Select Location</Text>
              <View style={styles.locationModalHeaderSpacer} />
            </View>

            <ScrollView style={styles.locationModalContent}>
              {/* Search Section */}
              <View style={styles.locationModalSection}>
                <Text style={styles.locationModalSectionTitle}>Search</Text>
                <View style={styles.locationModalSearchContainer}>
                  <View style={styles.locationModalSearchBar}>
                    <Icon
                      name="magnify"
                      size={20}
                      color={theme.colors.primary}
                      style={styles.locationModalSearchIcon}
                    />
                    <TextInput
                      spellCheck={true}
                      ref={searchInputRef}
                      placeholder="Search Australian locations..."
                      onChangeText={setLocationSearchQuery}
                      value={locationSearchQuery}
                      onSubmitEditing={pickTopSearchResult}
                      returnKeyType="search"
                      blurOnSubmit={false}
                      style={styles.locationModalSearchInput}
                      placeholderTextColor={theme.colors.onSurfaceVariant}
                      accessible={true}
                      accessibilityLabel="Search locations"
                      accessibilityHint="Type a suburb, then tap a suggestion to add it"
                      accessibilityRole="search"
                    />
                  </View>
                </View>
              </View>

              <SearchSuggestionList
                items={filteredLocations.filter(
                  (loc) => !selectedLocationsInModal.includes(loc),
                )}
                getKey={(loc) => loc}
                getLabel={(loc) => loc}
                onPick={pickLocationFromSearch}
              />

              {/* Selected Locations Section - Always show in multi-select mode */}
              {isLocationMultiSelect && (
                <View style={styles.locationModalSection}>
                  <Text style={styles.locationModalSectionTitle}>
                    Selected Locations ({selectedLocationsInModal.length})
                  </Text>
                  <View
                    style={[
                      styles.locationModalChipsContainer,
                      selectedLocationsInModal.length === 0 && { paddingBottom: 36 }
                    ]}
                  >
                    {selectedLocationsInModal.map((location, index) => (
                      <Chip
                        key={`selected-${index}`}
                        style={styles.locationModalSelectedChip}
                        textStyle={styles.locationModalSelectedChipText}
                        onClose={() => handleModalLocationSelect(location)}
                        closeIcon={() => <Icon name="x" size={16} color={theme.colors.onPrimary} />}
                      >
                        {location}
                      </Chip>
                    ))}
                  </View>
                </View>
              )}

              {/* All Locations Section */}
              <View style={styles.locationModalSection}>
                <Text style={styles.locationModalSectionTitle}>All Locations</Text>
                <View style={styles.locationModalChipsContainer}>
                  {australianLocations.map((location, index) => {
                    const isSelected = selectedLocationsInModal?.includes(location) || false;
                    return (
                    <FilterChip
                      key={index}
                      id={`all-${location}`}
                      label={location}
                      isSelected={isSelected}
                      onPress={() => handleLocationSelect(location)}
                      accessibilityHint={`Toggle ${location} location selection`}
                      size="large"
                      style={isSelected ? styles.locationModalChipSelected : styles.locationModalChip}
                      textStyle={isSelected ? styles.locationModalChipTextSelected : styles.locationModalChipText}
                    />
                    );
                  })}
                </View>
              </View>
            </ScrollView>

            {/* Done Button Footer - Only show in multi-select mode */}
            {isLocationMultiSelect && (
              <View style={styles.locationModalFooter}>
                <TouchableOpacity
                  style={[
                    styles.locationModalDoneButton,
                    selectedLocationsInModal.length === 0 && styles.locationModalDoneButtonDisabled,
                  ]}
                  onPress={() => {
                    if (selectedLocationsInModal.length > 0 && locationSelectCallback) {
                      locationSelectCallback(selectedLocationsInModal);
                    }
                    closeLocationModal();
                  }}
                  disabled={selectedLocationsInModal.length === 0}
                  accessible={true}
                  accessibilityLabel="Add selected locations"
                  accessibilityHint="Closes the location selection modal and adds all selected locations"
                  accessibilityRole="button"
                >
                  <Text style={styles.locationModalDoneButtonText}>
                    Add
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </Modal>

        {/* Testimonials Modal - Rendered at App level for proper overlay */}
        <Modal
          visible={showTestimonialsModal}
          animationType="slide"
          presentationStyle="overFullScreen"
          transparent={false}
          onRequestClose={() => {
            setShowTestimonialsModal(false);
            setShowTestimonialsSearch(false);
            setTestimonialsSearchQuery("");
            setModalRespondingToReviewId(null);
          }}
        >
          <Suspense fallback={null}>
          <View style={styles.testimonialsModalContainer}>
            {/* Modal Header */}
            <View style={styles.testimonialsModalHeader}>
              <TouchableOpacity
                onPress={resetTestimonialsModal}
                style={styles.testimonialsModalCloseButton}
                accessible={true}
                accessibilityLabel="Close testimonials modal"
                accessibilityHint="Closes the testimonials modal"
                accessibilityRole="button"
              >
                <Icon name="arrow-left" size={24} color="#FFFFFF" />
              </TouchableOpacity>
              <View style={styles.testimonialsModalHeaderText}>
                <Text style={styles.testimonialsModalTitle}>All Reviews</Text>
                <Text style={styles.testimonialsModalSubtitle}>
                  {filteredTestimonials.length} of {currentTestimonials.length} testimonials
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => setShowTestimonialsSearch(!showTestimonialsSearch)}
                style={styles.testimonialsModalSearchButton}
              >
                <Icon name="magnify" size={24} color="#FFFFFF" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.testimonialsModalContent}>
              {/* Search */}
              {showTestimonialsSearch && (
                <View style={styles.testimonialsModalSearchContainer}>
                  <TextInput
              spellCheck={true}
                    placeholder="Search testimonials..."
                    onChangeText={setTestimonialsSearchQuery}
                    value={testimonialsSearchQuery}
                    style={styles.testimonialsModalSearchInput}
                    placeholderTextColor={theme.colors.onSurfaceVariant}
                    accessible={true}
                    accessibilityLabel="Search testimonials"
                    accessibilityHint="Type to search through testimonials"
                    accessibilityRole="search"
                  />
                </View>
              )}

              {/* A "5 4 3 2 1 star" filter row sat here. With no scores there is
                  nothing to filter by, and every entry in this list is positive. */}

              {/* Testimonials List */}
              {filteredTestimonials.length > 0 ? (
                filteredTestimonials.map((testimonial, index) => (
                  <View
                    key={testimonial.id}
                    style={[
                      styles.testimonialsModalItem,
                      index === filteredTestimonials.length - 1 && styles.testimonialsModalItemLast,
                    ]}
                  >
                    <View style={styles.testimonialsModalItemHeader}>
                      <View style={styles.testimonialsModalItemByline}>
                        <ContactAvatar
                          uri={testimonial.avatar}
                          name={testimonial.name}
                          size={32}
                        />
                        <Text style={styles.testimonialsModalItemName}>{testimonial.name}</Text>
                      </View>
                    </View>
                    <Text style={styles.testimonialsModalItemDate}>{testimonial.date}</Text>
                    <Text style={styles.testimonialsModalItemComment}>{testimonial.comment}</Text>
                    {testimonial.providerResponse && (
                      <ProviderResponse response={testimonial.providerResponse} />
                    )}
                    {user && modalProviderId && user.id === modalProviderId && (
                      <View style={styles.testimonialsModalResponseActions}>
                        {!testimonial.providerResponse && modalRespondingToReviewId !== testimonial.id && (
                          <Button
                            mode="outlined"
                            onPress={() => setModalRespondingToReviewId(testimonial.id)}
                            style={styles.testimonialsModalRespondButton}
                            accessible={true}
                            accessibilityLabel="Respond to this review"
                            accessibilityHint="Opens response input for this review"
                          >
                            Respond
                          </Button>
                        )}
                        {testimonial.providerResponse && modalRespondingToReviewId !== testimonial.id && (
                          <Button
                            mode="outlined"
                            onPress={() => setModalRespondingToReviewId(testimonial.id)}
                            style={styles.testimonialsModalRespondButton}
                            accessible={true}
                            accessibilityLabel="Edit response"
                            accessibilityHint="Opens response editor"
                          >
                            Edit Response
                          </Button>
                        )}
                        {modalRespondingToReviewId === testimonial.id && (
                          <ProviderResponseInput
                            reviewId={testimonial.id}
                            providerName={modalProviderName}
                            onSubmit={async (text) => {
                              const res = await handleResponseSubmit(testimonial.id, text, modalProviderName);
                              // Closing regardless used to discard the wording on
                              // a failed save and leave no sign anything went wrong.
                              if (res && res.success === false) {
                                showAlert(
                                  "Response Failed",
                                  res.error || "Could not post your response. Please try again.",
                                );
                                return;
                              }
                              setModalRespondingToReviewId(null);
                            }}
                            onCancel={() => setModalRespondingToReviewId(null)}
                            existingResponse={testimonial.providerResponse}
                          />
                        )}
                      </View>
                    )}
                  </View>
                ))
              ) : (
                <View style={styles.testimonialsModalEmpty}>
                  <Text style={styles.testimonialsModalEmptyText}>
                    No testimonials match your search criteria.
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
          </Suspense>
        </Modal>

        {/* Services FAB - Positioned using screen dimensions */}
        {activeTab === "services" && (
          <AppFab
            style={[styles.servicesFab, {bottom: bottomMenuVisible ? bottomNavHeight + 54 : 98}]}
            onPress={handleFabPress}
            accessibilityLabel="Add new service"
            accessibilityHint="Opens menu to add new services to the platform"
          />
        )}

        {/* Skills Needed FAB - Positioned using screen dimensions */}
        {activeTab === "skills-needed" && (
          <AppFab
            style={[styles.servicesFab, {bottom: bottomMenuVisible ? bottomNavHeight + 54 : 98}]}
            onPress={handleFabPress}
            accessibilityLabel="Add new job"
            accessibilityHint="Opens menu to add new job posting"
          />
        )}

        {/* Services FAB Overlay - allows touches to pass through */}
        {activeTab === "services" && showAddMenu && (
          <View style={styles.servicesFabOverlay}>
            {/* Menu content would go here if needed */}
          </View>
        )}

        {/* Skills Needed FAB Overlay - allows touches to pass through */}
        {activeTab === "skills-needed" && showAddMenu && (
          <View style={styles.servicesFabOverlay}>
            {/* Menu content would go here if needed */}
          </View>
        )}

        {/* Hamburger Menu - Rendered at App level to avoid stacking context issues */}
        <HamburgerMenu
          activeMenuId={[
            "admin-dashboard", "pending-items", "user-management", "user-details", "waitlist-admin",
            "phone-checks", "invite-codes", "flagged-reviews", "flagged-listings", "analytics",
            "tech-stack", "ux-style-guide", "icon-reference",
          ].includes(activeTab) ? "administration" : activeTab}
          menuOptions={[
            {
              id: "settings",
              title: "Settings",
              icon: "settings",
              description: "Manage prefs",
            },
            ...(canPreviewSubscription ? [{
              id: "membership",
              title: "Subscription",
              icon: "credit-card",
              description: "Manage plan · private preview",
              hiddenReminder: true,
            }] : []),
            {
              id: "feedback",
              title: "Feedback",
              icon: "comment",
              description: "Talk to us",
            },
            {
              id: "about",
              title: "About",
              icon: "home",
              description: "Outback Exchange",
            },
            ...(user?.isAdmin
              ? [
                  {
                    id: "administration",
                    title: "Admin",
                    icon: "crown",
                    description: "Management",
                  },
                ]
              : []),
            {
              id: "sign-out",
              title: "Sign out",
              icon: "logout",
              description: "End session",
            },
          ]}
          onMenuItemPress={handleHamburgerMenuItem}
          iconColor={theme.colors.onPrimary}
          iconSize={30}
          isVisible={showHamburgerMenu}
          onClose={() => setShowHamburgerMenu(false)}
        />

        {/* Sign Out Confirmation Modal */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={showSignOutModal}
          onRequestClose={() => setShowSignOutModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Sign Out Confirmation</Text>
              <Text style={styles.modalMessage}>
                Are you sure you want to sign out?
              </Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity
                  style={[styles.modalButton, styles.cancelButton]}
                  onPress={() => setShowSignOutModal(false)}
                  accessible={true}
                  accessibilityLabel="Cancel sign out"
                  accessibilityHint="Stay logged in and close this dialog"
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalButton, styles.signOutButton]}
                  onPress={() => {
                    setShowSignOutModal(false);
                    handleSignOut();
                  }}
                  accessible={true}
                  accessibilityLabel="Confirm sign out"
                  accessibilityHint="Sign out of your account"
                >
                  <Text style={styles.signOutButtonText}>Sign Out</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
        </ContentContainer>
      </RootContainer>

      <UpdateBanner
        visible={updateReady}
        onUpdate={applyUpdate}
        onDismiss={dismissUpdate}
      />

      <InstallGuide
        visible={showInstallGuide}
        onDismiss={() => {
          setShowInstallGuide(false);
          // Re-checks whether there is still anything to nudge about, so the
          // banner goes away by itself once they've finished the steps.
          installNudge.recheck();
        }}
      />

      {/* Alert Modal */}
      <AlertModal
        visible={alertModal.visible}
        title={alertModal.title}
        message={alertModal.message}
        buttonText={alertModal.buttonText}
        cancelText={alertModal.cancelText}
        onConfirm={alertModal.onConfirm}
        destructive={alertModal.destructive}
        onDismiss={() => {
          const after = alertModal.onDismiss;
          setAlertModal({ visible: false, title: "", message: "" });
          if (after) after();
        }}
      />
    </PaperProvider>
  );
};

// styles imported from ./styles/appStyles

export default App;
